import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from './icons';
import { 
  ChevronLeft, 
  ChevronRight, 
  Trash2, 
  X, 
  RefreshCw,
  Bell,
  Calendar,
  Clock,
  Send,
  Check,
  AlertCircle,
  Star,
  User as UserIcon
} from './icons';
import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  where, 
  getDocs, 
  serverTimestamp,
  getDoc,
  Timestamp 
} from 'firebase/firestore';
import { auth } from '../firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { connectGoogleCalendar, loadGoogleIdentityServices } from '../utils/googleCalendar';
import { formatEventDateRange, getEventDisplayMeta, formatRangeNl } from '../utils/eventDisplay';
import PlanningChat from './planning/PlanningChat';
import PlanningAudioChannel from './planning/PlanningAudioChannel';
import { createProposal } from '../services/proposalService';

// ============================================================================
// MODULE-LEVEL UTILITIES
// ============================================================================

/** Round an ISO date string to the nearest minute (strips seconds + ms).
 *  Defined at module level so it can be used in both the Firestore listener
 *  and the isFavorite/isOtherUserFavorite callbacks without duplication. */
export const toMinuteMs = (iso: string): number =>
  Math.round(new Date(iso).getTime() / 60_000) * 60_000;

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

/** Source that produced the currently pending match in the planning panel.
 *  - favorites:      OTHER user starred MY block → they confirmed my time
 *  - favorites_sent: I starred THEIR block → I confirmed their time (I initiated)
 *  - autoplan:       overlap found via "Vrij voor netwerken" blocks
 *  - manual:         user manually selected a time slot on the calendar
 */
export type MatchSource = 'favorites' | 'favorites_sent' | 'autoplan' | 'manual';

export interface PendingMatch {
  source: MatchSource;
  slots: Array<{ start: Date; end: Date }>;
}

interface ProposedDate {
  date: string;
  time: string;
  endTime?: string;
}

interface MeetingProposal {
  id: string;
  fromUserId: string;
  fromUserName: string;
  toUserId: string;
  toUserName?: string;
  proposedDates: ProposedDate[];
  slots?: Array<{ start: string; end: string }>;
  message?: string;
  status: 'pending' | 'accepted' | 'declined' | 'counter' | 'sent';
  connectionId?: string;
  createdAt: Date;
  respondedAt?: Date;
  selectedDateIndex?: number;
  isCounterProposal?: boolean;
  originalProposalId?: string;
}

interface CounterProposal {
  fromUserId: string;
  fromUserName: string;
  slots: Array<{ start: string; end: string }>;
  createdAt: Date;
}

interface TimeSlotFavorite {
  id: string;
  userId: string;
  userName: string;
  start: string;
  end: string;
  createdAt: Date;
}

interface Notification {
  id: string;
  userId: string;
  type: 'proposal' | 'proposal_response' | 'meeting_confirmed' | 'reminder';
  title: string;
  message: string;
  data?: any;
  read: boolean;
  createdAt: Date;
}

interface ConnectionEntry {
  id: string;        // Firestore connection doc id
  userId: string;    // the other user's uid
  name: string;
  company?: string;
  photoURL?: string;
}

interface ExtendedAgendaProps {
  selectedConnectionData?: {
    id?: string;
    name?: string;
    photoURL?: string;
    user1Id?: string;
    user2Id?: string;
    availabilitySuggestions?: any[];
  };
  onAutoplan?: () => void;
  onProposal?: () => void;
  autoplanLoading?: boolean;
  connectionId?: string;
  planningLockResult?: {
    granted: boolean;
    ownerId: string | null;
    ownerName: string | null;
    phase: string | null;
    expiresAt: any;
  } | null;
  currentUserId?: string;
  onClosePlanning?: () => void;
  confirmedLocation?: { name: string; address: string } | null;
}

type PlanningMode = 'none' | 'manual' | 'autoplan' | 'proposal' | 'availabilityOnly';

interface PanelCtx {
  otherUsername: string;
  autoplanCount: number;
  proposalCount: number;
  availabilityCount: number;
  selectedRangeText?: string;
}

// ============================================================================
// PANEL COPY MAPPING (Centralized)
// ============================================================================
const PANEL_COPY: Record<PlanningMode, {
  title: string;
  instruction: string;
  emptyState: string;
  statusLine: (ctx: PanelCtx) => string;
}> = {
  none: {
    title: 'Planning',
    instruction: 'Kies een planningsmodus via de knoppen bovenaan.',
    emptyState: 'Geen modus actief. Kies "Handmatig" of "Autoplan".',
    statusLine: () => '',
  },
  manual: {
    title: 'Direct plannen',
    instruction: 'Klik eenmaal in de kalender op het gewenste tijdslot. Het geselecteerde tijdslot verschijnt dan hier.',
    emptyState: 'Nog geen tijdslot geselecteerd. Klik in de kalender.',
    statusLine: (ctx) => ctx.selectedRangeText
      ? `Geselecteerd: ${ctx.selectedRangeText}`
      : `Klik op een tijdslot in de kalender`,
  },
  autoplan: {
    title: 'Autoplan',
    instruction: 'Zoekt automatisch overlappende "Vrij voor netwerken" blokken in jullie agenda\'s. Beide gebruikers moeten groene beschikbaarheidsblokken hebben.',
    emptyState: 'Geen overlappende "Vrij voor netwerken" blokken gevonden. Voeg beschikbaarheid toe in je agenda of gebruik "Handmatig".',
    statusLine: (ctx) => ctx.autoplanCount > 0
      ? `${ctx.autoplanCount} overlappende momenten gevonden met ${ctx.otherUsername}`
      : `Zoekt overlappende momenten met ${ctx.otherUsername}`,
  },
  proposal: {
    title: 'Handmatig plannen',
    instruction: 'Klik op 1–3 tijdsblokken in de kalender. Zie elkaars blokken live en markeer een blok van de ander als favoriet om een match te maken.',
    emptyState: 'Klik op de kalender om tijdsblokken toe te voegen.',
    statusLine: (ctx) => `Jij: ${ctx.proposalCount}/3 blokken — ${ctx.otherUsername}: live zichtbaar`,
  },
  availabilityOnly: {
    title: 'Beschikbaarheid',
    instruction: 'Bekijk wanneer jullie beiden beschikbaar zijn voor netwerken.',
    emptyState: 'Geen beschikbare momenten.',
    statusLine: (ctx) => `Beschikbaar met ${ctx.otherUsername}: ${ctx.availabilityCount} momenten`,
  },
};

const parseRecurring = (raw: any) => {
  const toDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    return value.toDate ? value.toDate() : new Date(value);
  };

  if (raw?.recurring?.frequency) {
    const excludedRaw = Array.isArray(raw.recurring.excludedDates)
      ? raw.recurring.excludedDates
      : undefined;
    return {
      frequency: raw.recurring.frequency,
      endDate: toDate(raw.recurring.endDate),
      excludedDates: excludedRaw
        ? excludedRaw.map((d: any) => toDate(d)).filter(Boolean)
        : undefined
    };
  }

  const legacyFrequency = raw?.repeat;
  if (legacyFrequency && legacyFrequency !== 'none') {
    const excludedRaw = Array.isArray(raw?.excludedDates) ? raw.excludedDates : undefined;
    return {
      frequency: legacyFrequency,
      endDate: toDate(raw?.repeatEndDate),
      excludedDates: excludedRaw
        ? excludedRaw.map((d: any) => toDate(d)).filter(Boolean)
        : undefined
    };
  }

  return undefined;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ExtendedAgenda: React.FC<ExtendedAgendaProps> = ({
  selectedConnectionData,
  onAutoplan,
  onProposal,
  autoplanLoading = false,
  connectionId: propConnectionId,
  planningLockResult,
  currentUserId,
  onClosePlanning,
  confirmedLocation,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================
  const [user, setUser] = useState<User | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'biweek'>('week');
  const [hoveredEvent, setHoveredEvent] = useState<string | null>(null);
  const [editingEvent, setEditingEvent] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [hideAvailabilitySuggestions, setHideAvailabilitySuggestions] = useState(false);
  const [showAvailabilityOnly, setShowAvailabilityOnly] = useState(false);
  // Planning-mode layer toggles — both off by default for privacy
  const [showOwnAgenda, setShowOwnAgenda] = useState(false);
  const [showAvailabilityLayer, setShowAvailabilityLayer] = useState(false);

  // Time slots 06:00 - 24:00
  const timeSlots = useMemo(() => 
    Array.from({ length: 19 }, (_, i) => `${(i + 6).toString().padStart(2, '0')}:00`),
    []
  );

  // Google Calendar sync state
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState<{
    connected: boolean;
    lastSync: Date | null;
    syncing: boolean;
    eventCount: number;
  }>({
    connected: false,
    lastSync: null,
    syncing: false,
    eventCount: 0
  });
  const [googleReconnectNeeded, setGoogleReconnectNeeded] = useState<string | null>(null);
  const [indexErrors, setIndexErrors] = useState<{
    notifications?: { link?: string };
  }>({});

  // Toast & UI state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showMonthView, setShowMonthView] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#3B82F6');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showTimeInput, setShowTimeInput] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventStartTime, setNewEventStartTime] = useState('');
  const [newEventEndTime, setNewEventEndTime] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<any | null>(null);

  // Proposal & Notification State
  const [showProposalModal, setShowProposalModal] = useState(false);
  
  // Planning Lock State
  const [livePlanningLock, setLivePlanningLock] = useState<{
    ownerId: string | null;
    ownerName: string | null;
    phase: string | null;
    expiresAt: any;
  } | null>(null);
  
  // Determine if current user is the planning owner
  const isPlanningOwner = planningLockResult?.granted === true;
  const isObserver = planningLockResult && !planningLockResult.granted;
  const [proposalMessage, setProposalMessage] = useState('');
  const [proposedDates, setProposedDates] = useState<ProposedDate[]>([
    { date: '', time: '', endTime: '' },
    { date: '', time: '', endTime: '' }
  ]);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [incomingProposals, setIncomingProposals] = useState<MeetingProposal[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Confirmed location — always synced via real-time onSnapshot listener below
  const [localConfirmedLocation, setLocalConfirmedLocation] = useState<{ name: string; address: string } | null>(null);

  // Planning mode / UI state
  const [planningMode, setPlanningMode] = useState<PlanningMode>('proposal');
  const [sidePanelOpen, setSidePanelOpen] = useState(true);
  const [autoplanState, setAutoplanState] = useState<{ loading: boolean; results: any[]; error: string | null }>({
    loading: false,
    results: [],
    error: null,
  });
  const [proposalSlots, setProposalSlots] = useState<Array<{ start: string; end: string }>>([]);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [planningError, setPlanningError] = useState<string | null>(null);
  const [manualSelection, setManualSelection] = useState<{ start: Date; end: Date } | null>(null);
  const [editingSlotIndex, setEditingSlotIndex] = useState<number | null>(null);
  const [editingSlotData, setEditingSlotData] = useState<{ date: string; startTime: string; endTime: string } | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ date: Date; time: number } | null>(null);
  // Hover time indicator (floating label near cursor + horizontal guideline)
  const [gridHover, setGridHover] = useState<{ x: number; y: number; label: string } | null>(null);
  const [columnHoverY, setColumnHoverY] = useState<{ dayIndex: number; y: number } | null>(null);
  const autoplanInFlight = useRef(false);
  const autoplanSelectInFlight = useRef(false);
  const proposalSubmitInFlight = useRef(false);
  const proposalPanelRef = useRef<HTMLDivElement | null>(null);
  const manualInFlight = useRef(false);
  const autoplanCancelToken = useRef(0);
  const [internalAutoplanLoading, setInternalAutoplanLoading] = useState(false);

  // Counter-proposal state
  const [counterProposalMode, setCounterProposalMode] = useState(false);
  const [counterProposalTarget, setCounterProposalTarget] = useState<MeetingProposal | null>(null);
  const [counterSlots, setCounterSlots] = useState<Array<{ start: string; end: string }>>([]);

  // Send proposal state
  const [sendingProposal, setSendingProposal] = useState(false);

  // ── Connection Selector (standalone / plan-with picker) ──────────────────
  const [userConnections, setUserConnections] = useState<ConnectionEntry[]>([]);
  const [internalConnectionData, setInternalConnectionData] = useState<{
    id: string; name: string; photoURL?: string; user1Id: string; user2Id: string;
  } | null>(null);
  const [showConnectionPicker, setShowConnectionPicker] = useState(false);
  const [connectionSearch, setConnectionSearch] = useState('');
  const connectionPickerRef = useRef<HTMLDivElement>(null);
  const pickerButtonRef = useRef<HTMLButtonElement>(null);
  const [pickerDropPos, setPickerDropPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Live proposal tracking (shows pending proposals on calendar)
  const [sentProposals, setSentProposals] = useState<Array<{ id: string; slots: Array<{ start: string; end: string }>; status: string }>>([]);

  // Time slot favorites
  const [timeSlotFavorites, setTimeSlotFavorites] = useState<TimeSlotFavorite[]>([]);
  const [otherUserFavorites, setOtherUserFavorites] = useState<TimeSlotFavorite[]>([]);
  const [mutualTimeMatches, setMutualTimeMatches] = useState<Array<{ start: string; end: string }>>([]);

  // Live proposal slots from both users (stored in connection document)
  const [otherUserLiveSlots, setOtherUserLiveSlots] = useState<Array<{ start: string; end: string }>>([]);
  const prevMutualMatchCountRef = useRef(0);

  // ============================================================================
  // CONSTANTS
  // ============================================================================
  const colors = [
    { name: 'Blauw', value: '#3B82F6' },
    { name: 'Oranje', value: '#F97316' },
    { name: 'Groen', value: '#10B981' },
    { name: 'Paars', value: '#8B5CF6' },
    { name: 'Rood', value: '#EF4444' },
    { name: 'Geel', value: '#F59E0B' }
  ];

  const daysOfWeek = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
  const monthNames = [
    'januari', 'februari', 'maart', 'april', 'mei', 'juni',
    'juli', 'augustus', 'september', 'oktober', 'november', 'december'
  ];

  // ============================================================================
  // DERIVED VALUES
  // ============================================================================
  // internalConnectionData overrides prop-based data when user selects from picker
  const activeConnectionData = internalConnectionData ?? selectedConnectionData ?? null;
  const connectionId = propConnectionId || internalConnectionData?.id || selectedConnectionData?.id;
  
  const otherUserId = useMemo(() => {
    if (!activeConnectionData || !user?.uid) return undefined;
    return (activeConnectionData as any).user1Id === user.uid
      ? (activeConnectionData as any).user2Id
      : (activeConnectionData as any).user1Id;
  }, [activeConnectionData, user?.uid]);

  // Clean display name - only show the name, not the ID
  const otherUserName = useMemo(() => {
    const rawName = activeConnectionData?.name;
    if (!rawName) return 'Connectie';
    const cleanName = rawName.replace(/\s*[A-Za-z0-9]{20,}\s*/g, '').trim();
    return cleanName || rawName.split(/\s+/)[0] || 'Connectie';
  }, [activeConnectionData?.name]);

  // Photo of the other user (for avatar display)
  const otherUserPhoto = useMemo(() => {
    return (activeConnectionData as any)?.photoURL ?? null;
  }, [activeConnectionData]);

  // ── Unified pending match ─────────────────────────────────────────────────
  // Priority: favorites (★) > autoplan (Vrij voor netwerken) > manual.
  // Moved out of the side panel render IIFE so it is memoized and only
  // recomputed when the relevant state actually changes.
  const pendingMatch = useMemo((): PendingMatch | null => {
    const now = Date.now();

    // Case 1: Other user starred MY block → they confirmed a time I offered.
    // Show card for me (the block owner) so I can confirm the appointment.
    if (mutualTimeMatches.length > 0) {
      return {
        source: 'favorites',
        slots: mutualTimeMatches.slice(0, 3).map(m => ({
          start: new Date(m.start),
          end:   new Date(m.end),
        })),
      };
    }

    // Case 2: I starred THEIR block → I confirmed a time they offered.
    // Show card for me (the initiator) so I can also confirm the appointment.
    // Without this, only the block owner sees the match card — leaving the
    // person who clicked ★ without any confirmation UI.
    //
    // Guard: cross-reference with otherUserLiveSlots to ensure we only match
    // favorites that are actual blocks from the other user. This prevents a
    // false-positive if the user accidentally starred a time on the calendar
    // grid (double-click) that doesn't correspond to the other user's block.
    if (timeSlotFavorites.length > 0 && otherUserLiveSlots.length > 0) {
      const otherSlotKeys = new Set(
        otherUserLiveSlots.map(s => `${toMinuteMs(s.start)}_${toMinuteMs(s.end)}`)
      );
      const myStars = timeSlotFavorites
        .filter(f => {
          const start = new Date(f.start).getTime();
          if (isNaN(start) || start <= now) return false;
          // Only include this favorite if it matches one of the other user's actual blocks
          const key = `${toMinuteMs(f.start)}_${toMinuteMs(f.end)}`;
          return otherSlotKeys.has(key);
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        .slice(0, 3)
        .map(f => ({ start: new Date(f.start), end: new Date(f.end) }));
      if (myStars.length > 0) {
        return { source: 'favorites_sent', slots: myStars };
      }
    }

    if (autoplanState.results.length > 0 && !autoplanState.loading) {
      return {
        source: 'autoplan',
        slots: (autoplanState.results as Array<{ start: Date | string; end: Date | string }>).slice(0, 3).map(r => ({
          start: r.start instanceof Date ? r.start : new Date(r.start),
          end:   r.end   instanceof Date ? r.end   : new Date(r.end),
        })),
      };
    }
    if (manualSelection) {
      return {
        source: 'manual',
        slots: [{ start: manualSelection.start, end: manualSelection.end }],
      };
    }
    return null;
  }, [mutualTimeMatches, timeSlotFavorites, otherUserLiveSlots, autoplanState.results, autoplanState.loading, manualSelection]);

  // Per-source copy for the unified match card.
  const matchCopy = useMemo((): Record<MatchSource, { title: string; subtitle: string }> => ({
    favorites: {
      // The OTHER user starred my block → show their name
      title: `${otherUserName || 'De ander'} heeft jouw tijdvak gekozen!`,
      subtitle: 'Bevestig de afspraak met één klik',
    },
    favorites_sent: {
      // I starred their block → I initiated, still show confirmation card
      title: `Jij hebt een tijdvak gekozen!`,
      subtitle: `Bevestig de afspraak met ${otherUserName || 'je connectie'}`,
    },
    autoplan: {
      title: 'Jullie zijn allebei vrij op dit moment!',
      subtitle: 'Gevonden via Autoplan — bevestig direct',
    },
    manual: {
      title: 'Tijdvak geselecteerd',
      subtitle: 'Plan de afspraak op dit moment',
    },
  }), [otherUserName]);

  const indexWarnings = useMemo(() => {
    const warnings: Array<{ key: string; text: string; link?: string }> = [];
    if (indexErrors.notifications) {
      warnings.push({
        key: 'notifications',
        text: 'Firestore index ontbreekt voor notifications (userId + createdAt). Maak de index aan in Firebase Console.',
        link: indexErrors.notifications.link,
      });
    }
    return warnings;
  }, [indexErrors]);

  const formatSlotLabel = useCallback((slot: { start?: Date; end?: Date }) => {
    if (!slot?.start || !slot?.end) return 'Onbekend tijdslot';
    return `${slot.start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} • ${slot.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} - ${slot.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  }, []);

  const formatDateRangeNL = useCallback((start?: Date, end?: Date) => {
    if (!start || !end) return '';
    const date = start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    const startTime = start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    return `${date}, ${startTime} - ${endTime}`;
  }, []);

  const formatDuration = useCallback((start?: Date, end?: Date) => {
    if (!start || !end) return '';
    const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}u${m}` : `${h}u`;
  }, []);

  const getEventOtherName = useCallback((event: any) => {
    // Handle participantNames as object (new format)
    if (event.participantNames && typeof event.participantNames === 'object' && !Array.isArray(event.participantNames)) {
      const currentUid = user?.uid;
      if (currentUid && event.participants?.includes(currentUid)) {
        // Find the OTHER participant's name
        const otherUid = event.participants.find((uid: string) => uid !== currentUid);
        if (otherUid && event.participantNames[otherUid]) {
          return event.participantNames[otherUid];
        }
      }
    }
    // Fallback: handle legacy array format
    if (Array.isArray(event.participants) && Array.isArray(event.participantNames) && event.participants.length === event.participantNames.length) {
      const idx = event.participants.findIndex((p: string) => p !== user?.uid);
      if (idx >= 0) return event.participantNames[idx];
    }
    if (event.participantNames && Array.isArray(event.participantNames)) {
      const alt = event.participantNames.find((n: string) => n && n !== user?.displayName);
      if (alt) return alt;
    }
    return otherUserName || 'Connectie';
  }, [otherUserName, user?.displayName, user?.uid]);

  const isConnectieFlowEvent = useCallback((event: any) => {
    const titleMatch = typeof event.title === 'string' && /vrij voor netwerken/i.test(event.title);
    const hasConnectionFlag = event.connectionId || event.lockedToConnectionFlow || event.phase || event.source === 'connectieflow';
    return !!(hasConnectionFlag || titleMatch || event.eventType === 'networking_filled' || event.source === 'free_for_networking');
  }, []);

  // Helper: Check if event is a networking availability slot (green)
  const isNetworkingAvailabilityEvent = useCallback((event: any) => {
    return (
      event?.type === 'networking' &&
      event?.source === 'free_for_networking' &&
      event?.eventType !== 'networking_filled'
    );
  }, []);

  // Helper: Check if event is a filled networking appointment (purple)
  const isNetworkingFilledEvent = useCallback((event: any) => {
    return event?.eventType === 'networking_filled';
  }, []);

  // Helper: Convert grid click Y position to time with 15-min granularity
  const getTimeFromY = useCallback((yOffset: number): { hour: number; minute: number } => {
    // Each time slot is 25px high, starting from 06:00
    const slotIndex = Math.floor(yOffset / 25);
    const hour = 6 + slotIndex;
    
    // Calculate minute within the hour (15-min intervals)
    const yWithinSlot = yOffset % 25;
    const minuteQuarter = Math.floor(yWithinSlot / (25 / 4)); // 0, 1, 2, or 3
    const minute = minuteQuarter * 15; // 0, 15, 30, or 45
    
    return {
      hour: Math.min(Math.max(hour, 6), 23),
      minute: Math.min(minute, 45)
    };
  }, []);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================
  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    // Show longer for success messages (appointment confirmations)
    const duration = type === 'success' ? 5000 : 4000;
    setTimeout(() => setToast(null), duration);
  }, []);

  // Helper: Handle grid cell click for manual selection or proposal slot
  const handleGridClick = useCallback((date: Date, yOffset: number, e: React.MouseEvent) => {
    if (planningMode !== 'manual' && planningMode !== 'availabilityOnly' && planningMode !== 'proposal') return;
    
    const { hour, minute } = getTimeFromY(yOffset);
    const startTime = new Date(date);
    startTime.setHours(hour, minute, 0, 0);
    
    const endTime = new Date(startTime);
    endTime.setTime(startTime.getTime() + 60 * 60 * 1000); // Default 60 min
    
    if (planningMode === 'proposal') {
      const targetSlots = counterProposalMode ? counterSlots : proposalSlots;
      const setTargetSlots = counterProposalMode ? setCounterSlots : setProposalSlots;

      if (targetSlots.length >= 3) {
        showToast('Maximaal 3 momenten', 'info');
        return;
      }
      
      const newSlot = {
        start: startTime.toISOString(),
        end: endTime.toISOString(),
      };
      
      const isDuplicate = targetSlots.some(
        (s) => s.start === newSlot.start && s.end === newSlot.end
      );
      
      if (isDuplicate) {
        showToast('Dit moment is al toegevoegd', 'info');
        return;
      }
      
      setTargetSlots([...targetSlots, newSlot]);
      setSidePanelOpen(true); // open panel to show accumulated slots
      const newCount = targetSlots.length + 1;
      showToast(`Moment ${newCount}/3 toegevoegd`, newCount >= 3 ? 'success' : 'info');
    } else {
      // Manual or availabilityOnly mode: set selection and open panel
      setManualSelection({ start: startTime, end: endTime });
      setSidePanelOpen(true);
      showToast(`Geselecteerd: ${formatRangeNl(startTime, endTime)}`, 'info');
    }
  }, [planningMode, getTimeFromY, showToast, proposalSlots, counterProposalMode, counterSlots]);

  const isAutoplanLoading = autoplanLoading || internalAutoplanLoading;

  const getEventDisplayMeta = useCallback((event: any) => {
    const isNetworkingFilled = isConnectieFlowEvent(event);
    const color = isNetworkingFilled ? '#C084FC' : (event.color || '#DBEAFE');
    const borderColor = isNetworkingFilled ? '#A855F7' : 'rgba(0,0,0,0.1)';
    const badge = isNetworkingFilled ? 'Netwerken' : null;
    const otherName = isNetworkingFilled ? getEventOtherName(event) : undefined;
    const displayTitle = isNetworkingFilled ? `Netwerken met @${otherName || 'Connectie'}` : event.title;
    const durationLabel = isNetworkingFilled ? formatDuration(event.startTime, event.endTime) : '';
    const subtitle = isNetworkingFilled
      ? `ConnectieFlow${event.phase ? ` • ${event.phase}` : ''}${durationLabel ? ` • ${durationLabel}` : ''}`
      : '';
    return { color, borderColor, badge, displayTitle, subtitle };
  }, [formatDuration, getEventOtherName, isConnectieFlowEvent]);

  const openConnectionFlow = useCallback((connId?: string) => {
    const target = connId || connectionId;
    if (!target) {
      showToast('Geen connectie gevonden voor deze afspraak', 'info');
      return;
    }
    window.dispatchEvent(new CustomEvent('openConnectionFlow', { detail: { connectionId: target } }));
  }, [connectionId, showToast]);

  // Single-flight guard helper
  const guardOnce = useCallback(
    <T extends any[], R>(ref: React.MutableRefObject<boolean>, onBusy: () => void, fn: (...args: T) => Promise<R>) =>
      async (...args: T): Promise<R | undefined> => {
        if (ref.current) {
          onBusy();
          return;
        }
        ref.current = true;
        try {
          return await fn(...args);
        } finally {
          ref.current = false;
        }
      },
    []
  );

  const resetPlanning = useCallback(() => {
    setPlanningMode('none');
    setSidePanelOpen(false);
    setAutoplanState({ loading: false, results: [], error: null });
    setPlanningError(null);
    setProposalError(null);
    setProposalSlots([]);
    setShowOwnAgenda(false);
    setShowAvailabilityLayer(false);
    autoplanCancelToken.current++;
  }, []);

  useEffect(() => {
    if (planningMode !== 'proposal') return;
    if (!proposalPanelRef.current) return;
    try {
      proposalPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Proposal panel scroll failed', err);
      }
    }
  }, [planningMode]);

  const getWeekDates = useCallback((date: Date, biweek = false) => {
    const week: Date[] = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    const daysToShow = biweek ? 14 : 7;
    for (let i = 0; i < daysToShow; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      week.push(currentDay);
    }
    return week;
  }, []);

  const getMonthDates = useCallback((date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDay = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days: { date: Date; isCurrentMonth: boolean }[] = [];
    
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true });
    }
    
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }
    
    return days;
  }, []);

  const isToday = useCallback((date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const getTimeParts = useCallback((value: any): [number, number] => {
    if (typeof value === 'string') {
      const [h = '0', m = '0'] = value.split(':');
      return [parseInt(h, 10) || 0, parseInt(m, 10) || 0];
    }
    if (value?.toDate) {
      const d = value.toDate();
      return [d.getHours(), d.getMinutes()];
    }
    if (value instanceof Date) {
      return [value.getHours(), value.getMinutes()];
    }
    if (value?.seconds) {
      const d = new Date(value.seconds * 1000);
      return [d.getHours(), d.getMinutes()];
    }
    return [0, 0];
  }, []);

  const weekDates = useMemo(() => 
    getWeekDates(currentDate, viewMode === 'biweek'),
    [currentDate, viewMode, getWeekDates]
  );

  // ============================================================================
  // EFFECTS - Auth & Data Loading
  // ============================================================================

  // Real-time listener: always keep localConfirmedLocation up to date from the connection doc.
  // This covers all timing cases: prop arriving late, hot-reloads, other user selecting location.
  useEffect(() => {
    if (!connectionId) {
      // No connection yet — fall back to prop value if available
      setLocalConfirmedLocation(confirmedLocation ?? null);
      return;
    }
    const connRef = doc(db, 'connections', connectionId);
    const unsubscribe = onSnapshot(connRef, (snap) => {
      const data = snap.data();
      const selLoc = data?.physicalMeeting?.selectedLocation;
      if (selLoc?.location) {
        setLocalConfirmedLocation({
          name: selLoc.location.name || selLoc.location.address || 'Ontmoetingslocatie',
          address: selLoc.location.address || '',
        });
      } else {
        // No location in doc — keep prop value if it has one (don't override with null)
        setLocalConfirmedLocation(confirmedLocation ?? null);
      }
    }, (err) => {
      console.warn('[ExtendedAgenda] onSnapshot error for connection doc:', err);
      setLocalConfirmedLocation(confirmedLocation ?? null);
    });
    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (authUser) => {
      setUser(authUser);
    });
    return () => unsubscribe();
  }, []);

  // Cleanup guards on unmount
  useEffect(() => {
    return () => {
      autoplanCancelToken.current++;
      autoplanInFlight.current = false;
      proposalSubmitInFlight.current = false;
      manualInFlight.current = false;
    };
  }, []);

  // Load user's connections for the connection picker
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;

    const loadConnections = async () => {
      try {
        const [snap1, snap2] = await Promise.all([
          getDocs(query(
            collection(db, 'connections'),
            where('user1Id', '==', user.uid),
            where('status', '==', 'active')
          )),
          getDocs(query(
            collection(db, 'connections'),
            where('user2Id', '==', user.uid),
            where('status', '==', 'active')
          )),
        ]);

        if (cancelled) return;

        const entries: ConnectionEntry[] = [];
        const seen = new Set<string>();

        [...snap1.docs, ...snap2.docs].forEach((d) => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          const data = d.data();
          const isUser1 = data.user1Id === user.uid;
          entries.push({
            id: d.id,
            userId: isUser1 ? data.user2Id : data.user1Id,
            name: isUser1 ? (data.user2Name || 'Connectie') : (data.user1Name || 'Connectie'),
            company: isUser1 ? (data.user2Company || '') : (data.user1Company || ''),
            photoURL: isUser1 ? (data.user2Photo || data.user2PhotoURL || '') : (data.user1Photo || data.user1PhotoURL || ''),
          });
        });

        entries.sort((a, b) => a.name.localeCompare(b.name));
        setUserConnections(entries);
      } catch {
        // silent
      }
    };

    loadConnections();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Close connection picker on outside click
  useEffect(() => {
    if (!showConnectionPicker) return;
    const handler = (e: MouseEvent) => {
      if (connectionPickerRef.current && !connectionPickerRef.current.contains(e.target as Node)) {
        setShowConnectionPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showConnectionPicker]);

  useEffect(() => {
    loadGoogleIdentityServices();
  }, []);

  // Real-time planning lock listener (also fires when user picks connection internally)
  const activeListenConnectionId = propConnectionId || internalConnectionData?.id;
  useEffect(() => {
    if (!activeListenConnectionId) return;

    const unsubscribe = onSnapshot(
      doc(db, 'connections', activeListenConnectionId),
      (snapshot) => {
        if (!snapshot.exists()) return;
        
        const data = snapshot.data();
        const lockData = {
          ownerId: data.planningOwnerId || null,
          ownerName: data.planningOwnerName || null,
          phase: data.planningOwnerPhase || null,
          expiresAt: data.planningLockExpiresAt || null,
        };
        
        setLivePlanningLock(lockData);

        // Read other user's live proposal slots from connection document
        const currentUid = auth.currentUser?.uid;
        const tProposals = data.timeslotProposals || {};
        if (currentUid) {
          const otherSlots = Object.entries(tProposals)
            .filter(([uid]) => uid !== currentUid)
            .flatMap(([, slots]) => (slots as Array<{ start: string; end: string }>));
          setOtherUserLiveSlots(otherSlots);
        }
      },
      (error) => {
        console.error('[planning-lock] Listener error:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [activeListenConnectionId]);

  // Check Google Calendar connection status
  useEffect(() => {
    if (!user?.uid) return;

    const checkConnectionStatus = async () => {
      try {
        const userDocRef = doc(db, 'userProfiles', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const isConnected = userData.integrations?.googleCalendar || false;
          const lastSync = userData.integrations?.googleCalendarLastSync?.toDate() || null;
          const eventCount = userData.integrations?.googleCalendarEventCount || 0;

          setGoogleCalendarStatus(prev => ({
            ...prev,
            connected: isConnected,
            lastSync,
            eventCount
          }));
        }
      } catch (error) {
        console.error('Error checking Google Calendar status:', error);
      }
    };

    checkConnectionStatus();
  }, [user?.uid]);

  // Load calendar events
  useEffect(() => {
    if (!user?.uid) return;

    const calendarEventsRef = collection(db, 'calendarEvents');
    const calendarQ = query(
      calendarEventsRef,
      where('participants', 'array-contains', user.uid),
      orderBy('startTime', 'asc')
    );

    const unsubscribeCalendar = onSnapshot(calendarQ, (calendarSnapshot) => {
      const calendarEvents = calendarSnapshot.docs.map(docSnap => {
        const raw: any = docSnap.data();

        const recurring = parseRecurring(raw);

        return {
          id: docSnap.id,
          ...raw,
          date: raw.startTime?.toDate() || new Date(),
          title: raw.title || 'Geen titel',
          startTime: raw.startTime?.toDate() || new Date(),
          endTime: raw.endTime?.toDate() || new Date(),
          createdAt: raw.createdAt?.toDate() || new Date(),
          source: raw.source || 'superkonnected',
          recurring
        };
      });

      setEvents(calendarEvents);
    }, (error) => {
      console.error('Error in calendar events listener:', error);
    });

    return () => unsubscribeCalendar();
  }, [user?.uid]);

  // Listen for incoming meeting proposals
  useEffect(() => {
    if (!user?.uid) return;

    const proposalsRef = collection(db, 'meetingProposals');
    const q = query(
      proposalsRef,
      where('toUserId', '==', user.uid),
      where('status', 'in', ['pending', 'sent'])
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const proposals = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        // Normalize: proposals may use 'slots' or 'proposedDates' format
        let proposedDates: ProposedDate[] = data.proposedDates || [];
        if ((!proposedDates || proposedDates.length === 0) && Array.isArray(data.slots)) {
          proposedDates = data.slots.map((s: any) => ({
            date: s.start?.split?.('T')?.[0] || '',
            time: s.start?.split?.('T')?.[1]?.slice(0, 5) || '',
            endTime: s.end?.split?.('T')?.[1]?.slice(0, 5) || '',
          }));
        }
        return {
          id: docSnap.id,
          ...data,
          proposedDates,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          respondedAt: data.respondedAt?.toDate?.() || undefined,
        };
      }) as MeetingProposal[];

      // Filter out proposals where ALL suggested dates are in the past
      const now = new Date();
      const futureProposals = proposals.filter(p => {
        if (!p.proposedDates || p.proposedDates.length === 0) return true;
        return p.proposedDates.some(d => {
          if (!d.date) return true;
          const dateStr = d.endTime ? `${d.date}T${d.endTime}` : `${d.date}T23:59`;
          return new Date(dateStr) >= now;
        });
      });
      setIncomingProposals(futureProposals);
    }, (error) => {
      console.error('Error loading proposals:', error);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Listen for sent proposals (to show pending labels on calendar)
  useEffect(() => {
    if (!user?.uid || !connectionId) return;

    const sentRef = collection(db, 'meetingProposals');
    const q = query(
      sentRef,
      where('fromUserId', '==', user.uid),
      where('connectionId', '==', connectionId),
      where('status', '==', 'sent')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const proposals = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        slots: Array.isArray(docSnap.data().slots) ? docSnap.data().slots : [],
        status: docSnap.data().status || 'sent',
      }));
      setSentProposals(proposals);
    }, (error) => {
      console.warn('[ExtendedAgenda] sentProposals listener error (may need composite index):', error);
      setSentProposals([]);
    });

    return () => unsubscribe();
  }, [user?.uid, connectionId]);

  // On mount: restore previously saved proposal slots from Firestore
  useEffect(() => {
    if (!connectionId || !user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'connections', connectionId));
        if (cancelled || !snap.exists()) return;
        const saved: Array<{ start: string; end: string }> =
          snap.data()?.timeslotProposals?.[user.uid] || [];
        if (saved.length > 0) {
          setProposalSlots(saved);
          setPlanningMode('proposal');
          setSidePanelOpen(true);
          showToast(`📅 Je had nog ${saved.length} blok${saved.length > 1 ? 'ken' : ''} bewaard — ze zijn hersteld`, 'info');
        }
      } catch (e) {
        console.warn('[ExtendedAgenda] Could not restore proposal slots', e);
      }
    })();
    return () => { cancelled = true; };
  // Only run once on mount (connectionId + user.uid are stable identifiers)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionId, user?.uid]);

  // Auto-save proposalSlots to connection document in real-time (live sync)
  useEffect(() => {
    if (!connectionId || !user?.uid || planningMode !== 'proposal') return;
    const timer = setTimeout(async () => {
      try {
        await updateDoc(doc(db, 'connections', connectionId), {
          [`timeslotProposals.${user.uid}`]: proposalSlots,
        });
      } catch (e) {
        console.warn('[ExtendedAgenda] Could not auto-save proposal slots', e);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [proposalSlots, connectionId, user?.uid, planningMode]);

  // Toast when a new mutual timeslot match appears
  useEffect(() => {
    if (mutualTimeMatches.length > prevMutualMatchCountRef.current) {
      const match = mutualTimeMatches[mutualTimeMatches.length - 1];
      showToast(
        `🎉 Match! Jullie zijn het eens over ${formatRangeNl(new Date(match.start), new Date(match.end))}`,
        'success'
      );
      setSidePanelOpen(true);
    }
    prevMutualMatchCountRef.current = mutualTimeMatches.length;
  }, [mutualTimeMatches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for time slot favorites (own + other user)
  useEffect(() => {
    if (!user?.uid || !connectionId) return;

    const favRef = collection(db, 'connections', connectionId, 'timeSlotFavorites');
    const unsubscribe = onSnapshot(favRef, (snapshot) => {
      const allFavs: TimeSlotFavorite[] = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        userId: docSnap.data().userId,
        userName: docSnap.data().userName || '',
        start: docSnap.data().start,
        end: docSnap.data().end,
        createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
      }));

      const myFavs = allFavs.filter(f => f.userId === user.uid);
      const theirFavs = allFavs.filter(f => f.userId !== user.uid);

      setTimeSlotFavorites(myFavs);
      setOtherUserFavorites(theirFavs);

      // Match detection — FAVORITES (★) system.
      //
      // Rule: when the OTHER user has starred one of MY blocks, that is a match.
      // Creating a block = declaring availability. Starring = confirming availability.
      // One star from the other user on my block = two votes on the same moment = match.
      //
      // No comparison between two lists needed. theirFavs (their stars on my blocks)
      // ARE the matches, filtered to future slots and sorted soonest-first.
      //
      // NOTE: Autoplan ("Vrij voor netwerken" interval overlap) is a separate system
      //       in handleAutoplan and is not affected by this logic.
      const now = Date.now();
      const matches = theirFavs
        .filter(f => {
          const start = new Date(f.start).getTime();
          return !isNaN(start) && start > now;
        })
        .map(f => ({ start: f.start, end: f.end }))
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

      setMutualTimeMatches(matches);
    }, (error) => {
      console.warn('[ExtendedAgenda] timeSlotFavorites listener error:', error);
    });

    return () => unsubscribe();
  }, [user?.uid, connectionId]);

  // Listen for notifications
  useEffect(() => {
    if (!user?.uid) return;

    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date(),
      })) as Notification[];

      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    }, (error) => {
      console.error('Error loading notifications:', error);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // ============================================================================
  // EVENT FILTERING
  // ============================================================================
  const visibleEvents = useMemo(() => {
    // Privacy filter only activates when actively planning WITH another user.
    // Without a selected connection, always show the full calendar.
    const inPrivacyMode = planningMode !== 'none' && !!otherUserId;

    if (!inPrivacyMode && !showAvailabilityOnly) return events;

    // Privacy mode: respect the layer toggles (both off by default)
    if (inPrivacyMode) {
      return events.filter((e: any) => {
        const isAvailability = isNetworkingAvailabilityEvent(e);
        const isFilled = isNetworkingFilledEvent(e);

        // "Vrij voor netwerken" layer
        if (isAvailability || isFilled) return showAvailabilityLayer;

        // All other personal events (Google sync, activities, goals, etc.)
        return showOwnAgenda;
      });
    }

    // showAvailabilityOnly mode (Beschikbaar tab)
    return events.filter((e: any) => {
      const isAvailability = isNetworkingAvailabilityEvent(e);
      const isFilled = isNetworkingFilledEvent(e);
      if (!isAvailability && !isFilled) return false;
      if (otherUserId && Array.isArray(e.participants)) {
        const me = user?.uid;
        const hasMe = me ? e.participants.includes(me) : true;
        const hasOther = e.participants.includes(otherUserId);
        return hasMe && hasOther;
      }
      return true;
    });
  }, [events, showAvailabilityOnly, showOwnAgenda, showAvailabilityLayer, planningMode, user?.uid, otherUserId, isNetworkingAvailabilityEvent, isNetworkingFilledEvent]);

  const getEventsForDate = useCallback((date: Date) => {
    return visibleEvents.filter(event =>
      event.date.toDateString() === date.toDateString()
    );
  }, [visibleEvents]);

  const getUpcomingEvents = useCallback(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return [...visibleEvents]
      .filter(event => event.date >= todayStart && event.status !== 'done' && event.status !== 'completed')
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 10);
  }, [visibleEvents]);

  const getCompletedEventsByMonth = useCallback(() => {
    const completed = visibleEvents.filter((event: any) =>
      event.status === 'done' || event.status === 'completed'
    );
    const groups: { [key: string]: { label: string; items: any[] } } = {};

    completed.forEach(event => {
      const baseDate: Date = event.completedAt || event.date;
      const year = baseDate.getFullYear();
      const month = baseDate.getMonth();
      const key = `${year}-${month}`;
      const label = `${monthNames[month]} ${year}`;

      if (!groups[key]) {
        groups[key] = { label, items: [] };
      }
      groups[key].items.push(event);
    });

    return Object.entries(groups)
      .sort(([a], [b]) => (a > b ? -1 : 1))
      .map(([key, value]) => ({ key, ...value }));
  }, [visibleEvents, monthNames]);

  // ============================================================================
  // GOOGLE CALENDAR HANDLERS
  // ============================================================================
  const handleGoogleCalendarConnect = async () => {
    try {
      await connectGoogleCalendar();
      setGoogleCalendarStatus(prev => ({ ...prev, connected: true }));
      setGoogleReconnectNeeded(null);
      showToast('Google Calendar succesvol verbonden', 'success');
    } catch (error: any) {
      console.error('Error connecting Google Calendar:', error);
      showToast('Fout bij verbinden: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  const handleGoogleCalendarSync = async () => {
    if (!googleCalendarStatus.connected) {
      showToast('Verbind eerst je Google Calendar', 'info');
      return;
    }

    setGoogleCalendarStatus(prev => ({ ...prev, syncing: true }));

    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const syncGoogleCalendar = httpsCallable(functions, 'syncGoogleCalendar');
      const result = await syncGoogleCalendar({
        userId: user?.uid,
        syncDirection: 'both'
      });

      const data = result.data as { success?: boolean; ok?: boolean; eventCount?: number; error?: string; reason?: string };
      if (data.success || data.ok) {
        const eventCount = data.eventCount || 0;
        setGoogleCalendarStatus(prev => ({
          ...prev,
          syncing: false,
          lastSync: new Date(),
          eventCount
        }));
        setGoogleReconnectNeeded(null);
        showToast(`${eventCount} events gesynchroniseerd`, 'success');
      } else if (data.reason === 'INVALID_GOOGLE_CLIENT' || data.reason === 'GOOGLE_REFRESH_EXPIRED') {
        setGoogleCalendarStatus(prev => ({ ...prev, syncing: false, connected: false }));
        setGoogleReconnectNeeded(data.reason);
        showToast('Google Calendar verbinding ongeldig of verlopen. Koppel opnieuw.', 'error');
        return;
      } else {
        throw new Error(data.error || 'Sync mislukt');
      }
    } catch (error: any) {
      console.error('Error syncing Google Calendar:', error);
      setGoogleCalendarStatus(prev => ({ ...prev, syncing: false }));
      showToast('Fout bij synchroniseren: ' + (error.message || 'Onbekende fout'), 'error');
    }
  };

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================
  const navigateWeek = (direction: number) => {
    const newDate = new Date(currentDate);
    const daysToMove = viewMode === 'biweek' ? 14 : 7;
    newDate.setDate(currentDate.getDate() + (direction * daysToMove));
    setCurrentDate(newDate);
  };

  // ============================================================================
  // EVENT CRUD HANDLERS
  // ============================================================================
  const markEventAsDone = async (eventId: string) => {
    try {
      await updateDoc(doc(db, 'calendarEvents', eventId), {
        status: 'completed',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      showToast('Afspraak gemarkeerd als klaar', 'success');
    } catch (error) {
      console.error('Error marking event as done:', error);
      showToast('Fout bij markeren als klaar', 'error');
    }
  };

  const deleteEvent = async (eventId: string, scope: 'single' | 'series' = 'single', occurrenceDate?: Date) => {
    if (!user) return;
    const eventToUpdate = events.find((e: any) => e.id === eventId);
    if (!eventToUpdate) return;

    try {
      const eventRef = doc(db, 'calendarEvents', eventId);

      if (!eventToUpdate.recurring || scope === 'series') {
        await deleteDoc(eventRef);
        setHoveredEvent(null);
        showToast(scope === 'series' ? 'Hele reeks verwijderd' : 'Event verwijderd', 'success');
        return;
      }

      if (occurrenceDate) {
        const targetDateStr = occurrenceDate.toDateString();
        const existingExcluded = eventToUpdate.recurring?.excludedDates || [];
        const alreadyExcluded = existingExcluded.some((d: Date) => d.toDateString() === targetDateStr);
        if (alreadyExcluded) return;

        const newExcludedDate = new Date(
          occurrenceDate.getFullYear(),
          occurrenceDate.getMonth(),
          occurrenceDate.getDate()
        );
        const updatedExcluded = [...existingExcluded, newExcludedDate];

        await updateDoc(eventRef, {
          'recurring.excludedDates': updatedExcluded
        });

        setEvents((prev: any[]) =>
          prev.map(e =>
            e.id === eventId
              ? {
                  ...e,
                  recurring: {
                    ...e.recurring,
                    excludedDates: updatedExcluded
                  }
                }
              : e
          )
        );
        showToast('Occurrence verwijderd uit reeks', 'success');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showToast('Fout bij verwijderen', 'error');
    }
  };

  const saveEvent = async () => {
    if (!user || !editingEvent) return;
      try {
      const updateData: any = {
          title: editingEvent.title,
        description: editingEvent.description || '',
        updatedAt: serverTimestamp()
      };

      if (editingEvent.startTime) {
        updateData.startTime = editingEvent.startTime instanceof Date
          ? editingEvent.startTime
          : new Date(editingEvent.startTime);
      }
      if (editingEvent.endTime) {
        updateData.endTime = editingEvent.endTime instanceof Date
          ? editingEvent.endTime
          : new Date(editingEvent.endTime);
      }

      await updateDoc(doc(db, 'calendarEvents', editingEvent.id), updateData);
    setEditingEvent(null);
      showToast('Event bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating event:', error);
      showToast('Fout bij bijwerken', 'error');
    }
  };

  const handleDateClick = (date: Date) => {
    if (planningMode === 'manual') {
      setSelectedDate(date);
      setShowTimeInput(true);
      setSidePanelOpen(true);
      showToast('Tijd geselecteerd, vul de details in', 'info');
      return;
    }
    if (planningMode === 'proposal') {
      if (proposalSlots.length >= 3) {
        showToast('Maximaal 3 momenten', 'info');
        return;
      }
      const startISO = new Date(date.setHours(9, 0, 0, 0)).toISOString();
      const endISO = new Date(date.setHours(9, 30, 0, 0)).toISOString();
      const exists = proposalSlots.some(s => s.start === startISO && s.end === endISO);
      if (!exists) {
        const next = [...proposalSlots, { start: startISO, end: endISO }];
        setProposalSlots(next);
        // Houd bestaande proposedDates in sync
        setProposedDates(next.map(s => ({
          date: s.start.split('T')[0],
          time: s.start.split('T')[1].slice(0,5),
          endTime: s.end.split('T')[1].slice(0,5),
        })));
      }
      setSidePanelOpen(true);
      return;
    }
    // default
    setSelectedDate(date);
    setShowTimeInput(true);
  };

  const addNewEvent = async () => {
    if (!newEventTitle || !newEventStartTime || !newEventEndTime || !selectedDate || !user) return;

    try {
      const startDateTime = new Date(selectedDate);
      const [startHour, startMinute] = newEventStartTime.split(':').map(Number);
      startDateTime.setHours(startHour, startMinute, 0, 0);

      const endDateTime = new Date(selectedDate);
      const [endHour, endMinute] = newEventEndTime.split(':').map(Number);
      endDateTime.setHours(endHour, endMinute, 0, 0);

      await addDoc(collection(db, 'calendarEvents'), {
          title: newEventTitle,
          description: '',
        startTime: startDateTime,
        endTime: endDateTime,
        type: 'personal',
        category: 'work',
        participants: [user.uid],
        participantNames: [user.displayName || ''],
        status: 'scheduled',
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        reminderSent: false,
          color: selectedColor,
        source: 'superkonnected',
        connectionId: connectionId || undefined,
      });

      // Clear planning_pending reminders if this was for a connection
      if (connectionId) {
        try {
          const remindersQuery = query(
            collection(db, 'inboxItems'),
            where('type', '==', 'planning_pending'),
            where('connectionId', '==', connectionId)
          );
          const remindersSnapshot = await getDocs(remindersQuery);
          const updatePromises = remindersSnapshot.docs.map(d => 
            updateDoc(doc(db, 'inboxItems', d.id), {
              isRead: true,
              resolvedAt: serverTimestamp(),
            })
          );
          await Promise.all(updatePromises);
          console.log('[planning-lock] Cleared reminders after event created');
        } catch (err) {
          console.warn('Kon reminders niet clearen', err);
        }
      }

      // Release planning owner lock
      if (connectionId && user?.uid) {
        try {
          const { releasePlanningLock } = await import('../services/connectionJourneyService');
          await releasePlanningLock(connectionId, user.uid);
          console.log('[planning-lock] Released lock after event created');
        } catch (err) {
          console.warn('Kon lock niet releasen', err);
        }
      }
        
        // Reset form
        setNewEventTitle('');
        setNewEventStartTime('');
        setNewEventEndTime('');
        setSelectedDate(null);
        setShowTimeInput(false);
        setShowMonthView(false);
      showToast('Afspraak toegevoegd', 'success');
      } catch (error) {
        console.error('Error adding event:', error);
      showToast('Fout bij toevoegen afspraak', 'error');
    }
  };

  // ============================================================================
  // SEND PROPOSAL HANDLER (async proposal flow to inbox)
  // ============================================================================
  const handleSendProposal = useCallback(async () => {
    if (!user?.uid || !connectionId || !otherUserId || proposalSlots.length < 1) return;
    if (sendingProposal) return;
    setSendingProposal(true);
    try {
      const slots = proposalSlots.map(s => ({ startTime: new Date(s.start), endTime: new Date(s.end) }));
      const proposalRef = await createProposal(connectionId, user.uid, otherUserId, 'physical', slots);

      // Fetch phone number of current user to share on proposal card
      let fromPhone: string | undefined;
      try {
        const profileSnap = await getDoc(doc(db, 'userProfiles', user.uid));
        if (profileSnap.exists()) {
          const pd = profileSnap.data();
          fromPhone = pd.phone || pd.phoneNumber || undefined;
        }
      } catch (_) {}

      // Create inbox item for receiver
      await addDoc(collection(db, 'inboxItems'), {
        userId: otherUserId,
        type: 'proposal_received',
        category: 'planning',
        title: `${user.displayName || 'Connectie'} stuurde een voorstel`,
        message: `${user.displayName || 'Je connectie'} heeft ${proposalSlots.length} tijdstip${proposalSlots.length === 1 ? '' : 'pen'} voorgesteld.`,
        isRead: false,
        priority: 'high',
        createdAt: serverTimestamp(),
        fromUserId: user.uid,
        fromUserName: user.displayName || 'Connectie',
        fromPhone: fromPhone || null,
        data: {
          proposalId: proposalRef?.id || null,
          connectionId,
          slots: proposalSlots,
        },
      });

      showToast(`Voorstel verstuurd naar ${otherUserName || 'je connectie'}!`, 'success');
      setProposalSlots([]);
    } catch (err) {
      console.error('send proposal error', err);
      showToast('Fout bij versturen voorstel', 'error');
    } finally {
      setSendingProposal(false);
    }
  }, [user, connectionId, otherUserId, otherUserName, proposalSlots, sendingProposal, showToast]);

  // ============================================================================
  // AUTOPLAN HANDLER
  // ============================================================================
  const handleAutoplan = useCallback(async () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[agenda] autoplan click', { connectionId, hasConnection: !!otherUserId });
    }
    if (autoplanInFlight.current) {
      showToast('Bezig, even wachten', 'info');
      return;
    }
    if (!user?.uid || !otherUserId) {
      setPlanningMode('autoplan');
      setSidePanelOpen(true);
      setAutoplanState({ loading: false, results: [], error: 'Selecteer eerst een connectie om automatisch te plannen' });
      setPlanningError('Selecteer eerst een connectie om automatisch te plannen');
      showToast('Selecteer eerst een connectie om automatisch te plannen', 'info');
      return;
    }

    setPlanningMode('autoplan');
    setSidePanelOpen(true);
    setPlanningError(null);
    setAutoplanState({ loading: true, results: [], error: null });
    autoplanInFlight.current = true;
    const token = ++autoplanCancelToken.current;

    try {
      const myEventsQuery = query(
        collection(db, 'calendarEvents'),
        where('participants', 'array-contains', user.uid),
        where('type', '==', 'networking')
      );
      const otherEventsQuery = query(
        collection(db, 'calendarEvents'),
        where('participants', 'array-contains', otherUserId),
        where('type', '==', 'networking')
      );

      const [mySnapshot, otherSnapshot] = await Promise.all([
        getDocs(myEventsQuery),
        getDocs(otherEventsQuery)
      ]);

      const myAvailability = mySnapshot.docs.map(d => ({
        start: d.data().startTime?.toDate(),
        end: d.data().endTime?.toDate()
      })).filter(a => a.start && a.end);

      const otherAvailability = otherSnapshot.docs.map(d => ({
        start: d.data().startTime?.toDate(),
        end: d.data().endTime?.toDate()
      })).filter(a => a.start && a.end);

      const overlaps: { start: Date; end: Date; confidence: number }[] = [];
      const now = new Date();

      myAvailability.forEach(mySlot => {
        if (mySlot.start < now) return; // skip my past slots
        otherAvailability.forEach(otherSlot => {
          if (otherSlot.start < now) return; // skip other user's past slots too
          const overlapStart = new Date(Math.max(mySlot.start.getTime(), otherSlot.start.getTime()));
          const overlapEnd = new Date(Math.min(mySlot.end.getTime(), otherSlot.end.getTime()));
          if (overlapStart < overlapEnd && overlapStart >= now) { // overlap must be in the future
            const durationMinutes = (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60);
            if (durationMinutes >= 30) {
              overlaps.push({
                start: overlapStart,
                end: overlapEnd,
                confidence: Math.min(100, Math.round(durationMinutes / 60 * 100))
              });
            }
          }
        });
      });

      // Deduplicate overlaps by exact start/end to avoid showing duplicates
      const dedupMap = new Map<string, { start: Date; end: Date; confidence: number }>();
      overlaps.forEach(o => {
        const key = `${o.start.toISOString()}_${o.end.toISOString()}`;
        if (!dedupMap.has(key)) {
          dedupMap.set(key, o);
        } else {
          // Keep the one with higher confidence if duplicates arise
          const existing = dedupMap.get(key)!;
          if (o.confidence > existing.confidence) {
            dedupMap.set(key, o);
          }
        }
      });
      const uniqueOverlaps = Array.from(dedupMap.values());

      if (token !== autoplanCancelToken.current) return;

      const myFutureSlots = myAvailability.filter(a => a.start >= new Date());
      const otherFutureSlots = otherAvailability.filter(a => a.start >= new Date());

      if (myFutureSlots.length === 0 && otherFutureSlots.length === 0) {
        const msg = 'Jullie hebben allebei nog geen "Vrij voor netwerken" blokken ingesteld. Voeg groene beschikbaarheidsblokken toe in je agenda.';
        setAutoplanState({ loading: false, results: [], error: msg });
        showToast('📅 Geen beschikbaarheid gevonden — voeg "Vrij voor netwerken" blokken toe in je agenda', 'info');
        autoplanInFlight.current = false;
        return;
      }
      if (myFutureSlots.length === 0) {
        const msg = 'Je hebt zelf nog geen "Vrij voor netwerken" blokken ingesteld. Klik op een dag in de kalender en voeg een groen beschikbaarheidsblok toe.';
        setAutoplanState({ loading: false, results: [], error: msg });
        showToast('📅 Jij hebt geen "Vrij voor netwerken" blokken — stel je beschikbaarheid in via de kalender', 'info');
        autoplanInFlight.current = false;
        return;
      }
      if (otherFutureSlots.length === 0) {
        const otherName = otherUserName || 'Je connectie';
        const msg = `${otherName} heeft nog geen "Vrij voor netwerken" blokken ingesteld. Vraag ${otherName} om beschikbaarheid toe te voegen.`;
        setAutoplanState({ loading: false, results: [], error: msg });
        showToast(`📅 ${otherName} heeft nog geen beschikbaarheid ingesteld`, 'info');
        autoplanInFlight.current = false;
        return;
      }

      if (uniqueOverlaps.length === 0) {
        setAutoplanState({ loading: false, results: [], error: 'Geen overlappende "Vrij voor netwerken" blokken gevonden. Controleer of jullie beschikbaarheid op dezelfde momenten staat, of gebruik "Handmatig" plannen.' });
        showToast('Geen overlap gevonden — jullie beschikbaarheid overlapt nog niet', 'info');
        // Notify the other user: no match found
        if (otherUserId && connectionId && user?.uid) {
          addDoc(collection(db, 'planningNotifications'), {
            toUserId: otherUserId,
            fromUserId: user.uid,
            fromUserName: user.displayName || 'Je connectie',
            connectionId,
            type: 'autoplan_no_match',
            message: `${user.displayName || 'Je connectie'} heeft Autoplan geprobeerd maar er is nog geen overlappende beschikbaarheid gevonden.`,
            read: false,
            createdAt: serverTimestamp(),
          }).catch(() => {});
        }
      } else {
        uniqueOverlaps.sort((a, b) => a.start.getTime() - b.start.getTime());
        const bestSlots = uniqueOverlaps.slice(0, 3);
        setAutoplanState({ loading: false, results: bestSlots, error: null });
        showToast(`${bestSlots.length} overlappende tijdslot${bestSlots.length > 1 ? 's' : ''} gevonden!`, 'success');
        // Notify the other user: match found, about to plan
        if (otherUserId && connectionId && user?.uid && bestSlots.length > 0) {
          const firstSlot = bestSlots[0] as { start: Date; end: Date };
          const slotLabel = firstSlot.start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' }) +
            ' ' + firstSlot.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) +
            '–' + firstSlot.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
          addDoc(collection(db, 'planningNotifications'), {
            toUserId: otherUserId,
            fromUserId: user.uid,
            fromUserName: user.displayName || 'Je connectie',
            connectionId,
            type: 'autoplan_match_found',
            locationName: slotLabel,
            matchCount: bestSlots.length,
            message: `${user.displayName || 'Je connectie'} heeft ${bestSlots.length} overlappend${bestSlots.length > 1 ? 'e momenten' : ' moment'} gevonden (bijv. ${slotLabel}). De afspraak wordt binnenkort bevestigd.`,
            read: false,
            createdAt: serverTimestamp(),
          }).catch(() => {});
        }
      }
    } catch (error) {
      if (token !== autoplanCancelToken.current) return;
      console.error('Autoplan error:', error);
      setAutoplanState({ loading: false, results: [], error: 'Autoplan lukt nu niet' });
      setPlanningError('Autoplan lukt nu niet');
      showToast('Fout bij automatisch plannen', 'error');
    } finally {
      if (token === autoplanCancelToken.current) {
        setInternalAutoplanLoading(false);
        autoplanInFlight.current = false;
        setAutoplanState(prev => ({ ...prev, loading: false }));
      }
    }
  }, [connectionId, otherUserId, user?.uid, showToast]);

  const handleAutoplanSelect = useMemo(() =>
    guardOnce(
      autoplanSelectInFlight,
      () => showToast('Bezig, even wachten', 'info'),
      async (slot: { start: Date; end: Date }) => {
        if (!user?.uid || !otherUserId) {
          showToast('Geen connectie geselecteerd', 'error');
          return;
        }
        const fallbackName = getEventOtherName({});
        const connectieName = otherUserName || fallbackName || 'Connectie';

        try {
          const { createAppointment } = await import('../services/appointmentService');
          const { eventId } = await createAppointment({
            start: slot.start,
            end: slot.end,
            creatorUid: user.uid,
            creatorDisplayName: user.displayName || 'Ik',
            otherUserId,
            otherUserName: connectieName,
            connectionId: connectionId ?? null,
            locationName: localConfirmedLocation?.name ?? null,
            locationAddress: localConfirmedLocation?.address ?? null,
            dateRangeText: formatDateRangeNL(slot.start, slot.end),
          });

          const successMessage = `Afspraak bevestigd voor ${slot.start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })} om ${slot.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
          showToast(successMessage, 'success');
          setPlanningMode('none');

          window.dispatchEvent(new CustomEvent('appointment-planned', {
            detail: { message: successMessage, type: 'success', slot, eventId }
          }));

          setTimeout(() => {
            onClosePlanning?.();
          }, 3000);
        } catch (error) {
          console.error('[handleAutoplanSelect] createAppointment failed:', error);
          showToast('Fout bij inplannen. Probeer het opnieuw.', 'error');
        }
      }
    ), [guardOnce, otherUserId, connectionId, otherUserName, showToast, user?.uid, user?.displayName, localConfirmedLocation]);

  // ============================================================================
  // MANUAL PLAN HANDLER
  // ============================================================================
  const handlePlanManual = useMemo(() =>
    guardOnce(
      manualInFlight,
      () => showToast('Bezig, even wachten', 'info'),
      async () => {
        if (!manualSelection) {
          showToast('Geen tijdslot geselecteerd', 'error');
          return;
        }
        if (!user?.uid || !otherUserId) {
          showToast('Geen connectie geselecteerd', 'error');
          return;
        }
        if (manualSelection.start >= manualSelection.end) {
          showToast('Ongeldige tijdreeks', 'error');
          return;
        }

        const start = manualSelection.start;
        const end = manualSelection.end;
        const rangeText = formatRangeNl(start, end);
        const currentUserName = user.displayName || 'Connectie';
        const otherUsername = otherUserName || 'Connectie';

        try {
          // Check if event already exists (prevent duplicates)
          const existingEventsQuery = query(
            collection(db, 'calendarEvents'),
            where('participants', 'array-contains', user.uid),
            where('type', '==', 'networking')
          );
          const existingSnapshot = await getDocs(existingEventsQuery);
          const isDuplicate = existingSnapshot.docs.some(doc => {
            const data = doc.data();
            const docStart = data.startTime?.toDate?.() || new Date(data.startTime);
            const docEnd = data.endTime?.toDate?.() || new Date(data.endTime);
            return (
              docStart.getTime() === start.getTime() &&
              docEnd.getTime() === end.getTime() &&
              data.participants?.includes(otherUserId)
            );
          });
          
          if (isDuplicate) {
            showToast('Deze afspraak bestaat al', 'info');
            setManualSelection(null);
            return;
          }
          
          // 1. Create calendar event (title is generic, display logic uses participantNames)
          const eventDoc = await addDoc(collection(db, 'calendarEvents'), {
            title: 'Netwerken',
            description: 'Gepland via Plan handmatig',
            startTime: Timestamp.fromDate(start),
            endTime: Timestamp.fromDate(end),
            type: 'networking',
            source: 'free_for_networking',
            eventType: 'networking_filled',
            categoryColor: 'purple',
            color: '#C084FC',
            participants: [user.uid, otherUserId],
            participantNames: {
              [user.uid]: currentUserName,
              [otherUserId]: otherUsername,
            },
            connectionId: connectionId,
            createdBy: user.uid,
            createdAt: serverTimestamp(),
            status: 'scheduled',
          });

          // 2. Create inbox item for other user
          await addDoc(collection(db, 'inboxItems'), {
            userId: otherUserId,
            type: 'meeting_scheduled',
            category: 'planning',
            title: `Afspraak ingepland met ${currentUserName}`,
            subtitle: rangeText,
            preview: rangeText,
            fromUserId: user.uid,
            fromUserName: currentUserName,
            data: {
              eventId: eventDoc.id,
              connectionId: connectionId,
              otherUserName: currentUserName,
              start: start.toISOString(),
              end: end.toISOString(),
            },
            isRead: false,
            createdAt: serverTimestamp(),
          });

          // 3. Create notification for other user
          await addDoc(collection(db, 'notifications'), {
            userId: otherUserId,
            type: 'meeting_confirmed',
            title: `📅 Afspraak ingepland`,
            message: `Je hebt een afspraak ingepland met ${currentUserName} op ${rangeText}.`,
            data: {
              eventId: eventDoc.id,
              connectionId: connectionId,
              otherUserName: currentUserName,
              start: start.toISOString(),
              end: end.toISOString(),
            },
            read: false,
            createdAt: serverTimestamp(),
          });

          // 4. Update planning lock (optional)
          if (connectionId) {
            try {
              await updateDoc(doc(db, 'connections', connectionId), {
                planningLock: {
                  status: 'scheduled',
                  lockedByUid: user.uid,
                  lockedByUsername: currentUserName,
                  lockReason: 'manual_plan',
                  lockedAt: serverTimestamp(),
                  expiresAt: new Date(Date.now() + 15 * 60 * 1000),
                },
              });
            } catch (err) {
              console.warn('Kon planningLock niet updaten', err);
            }
          }

          // 5. Clear planning_pending reminders for this connection
          if (connectionId) {
            try {
              const remindersQuery = query(
                collection(db, 'inboxItems'),
                where('type', '==', 'planning_pending'),
                where('connectionId', '==', connectionId)
              );
              const remindersSnapshot = await getDocs(remindersQuery);
              const deletePromises = remindersSnapshot.docs.map(d => 
                updateDoc(doc(db, 'inboxItems', d.id), {
                  isRead: true,
                  resolvedAt: serverTimestamp(),
                  resolvedReason: 'planning_completed',
                })
              );
              await Promise.all(deletePromises);
              console.log('[planning-lock] Cleared reminders after manual planning completed');
            } catch (err) {
              console.warn('Kon reminders niet clearen', err);
            }
          }

          showToast(`Verzonden: ${rangeText}`, 'success');
          setManualSelection(null); // Reset selection
          setPlanningMode('none');
          setSidePanelOpen(false);
        } catch (error) {
          console.error('Error creating manual plan event:', error);
          showToast('Fout bij inplannen', 'error');
        }
      }
    ), [guardOnce, manualSelection, user?.uid, user?.displayName, otherUserId, otherUserName, connectionId, showToast]);

  // ============================================================================
  // PROPOSAL HANDLERS
  // ============================================================================
  const handleOpenProposalModal = () => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[agenda] proposal click', { connectionId, hasConnection: !!otherUserId });
    }
    setPlanningMode('proposal');
    setSidePanelOpen(true);
    setProposalError(null);
    setShowProposalModal(!!otherUserId);
    if (!otherUserId) {
      showToast('Selecteer eerst een connectie om handmatig te plannen', 'info');
    } else {
      showToast('Klik op 1–3 tijdsblokken in de kalender. Markeer blokken van je connectie als favoriet om een match te maken!', 'info');
    }
  };

  const addProposedDate = () => {
    setProposedDates([...proposedDates, { date: '', time: '', endTime: '' }]);
  };

  const removeProposedDate = (index: number) => {
    if (proposedDates.length > 1) {
      setProposedDates(proposedDates.filter((_, i) => i !== index));
    }
  };

  const updateProposedDate = (index: number, field: keyof ProposedDate, value: string) => {
    const updated = [...proposedDates];
    updated[index][field] = value;
    setProposedDates(updated);
  };

  const sendProposal = async () => {
    // Validate
    if (!user?.uid || !otherUserId) {
      showToast('Geen connectie geselecteerd', 'error');
      return;
    }

    if (proposalSlots.length < 2) {
      showToast('Kies minimaal 2 momenten', 'error');
      setProposalError('Kies minimaal 2 momenten');
      return;
    }

    if (proposalSlots.length > 3) {
      showToast('Maximaal 3 momenten toegestaan', 'error');
      setProposalError('Maximaal 3 momenten toegestaan');
      return;
    }

    setProposalLoading(true);
    setProposalError(null);

    const currentUserName = user.displayName || 'Connectie';
    const otherUsername = otherUserName || 'Connectie';

    try {
      // Prepare slots payload
      const slotsPayload = proposalSlots.map(s => ({
        start: s.start,
        end: s.end,
      }));

      // Create formatted slot texts for inbox/notification
      const slotTexts = proposalSlots.map(s => 
        formatRangeNl(new Date(s.start), new Date(s.end))
      );
      const slotsSummaryShort = slotTexts.join(' • ');

      // 1. Create meeting proposal document
      const proposalRef = await addDoc(collection(db, 'meetingProposals'), {
        connectionId: connectionId,
        fromUserId: user.uid,
        fromUserName: currentUserName,
        toUserId: otherUserId,
        toUserName: otherUsername,
        slots: slotsPayload,
        status: 'sent',
        createdAt: serverTimestamp(),
      });

      // 2. Create inbox item for user2
      await addDoc(collection(db, 'inboxItems'), {
        userId: otherUserId,
        type: 'meeting_proposal',
        category: 'planning',
        title: `Voorstel voor afspraak met ${currentUserName}`,
        subtitle: slotsSummaryShort,
        preview: slotsSummaryShort,
        fromUserId: user.uid,
        fromUserName: currentUserName,
        data: {
          proposalId: proposalRef.id,
          connectionId: connectionId,
          slots: slotsPayload,
        },
        isRead: false,
        createdAt: serverTimestamp(),
      });

      // 3. Create notification for user2
      await addDoc(collection(db, 'notifications'), {
        userId: otherUserId,
        type: 'meeting_proposal',
        title: `🕒 Voorstel ontvangen`,
        message: `${currentUserName} stelde deze momenten voor: ${slotsSummaryShort}`,
        data: {
          proposalId: proposalRef.id,
          connectionId: connectionId,
          slots: slotsPayload,
        },
        read: false,
        createdAt: serverTimestamp(),
      });

      // 4. Update planning lock (optional)
      if (connectionId) {
        try {
          await updateDoc(doc(db, 'connections', connectionId), {
            planningLock: {
              status: 'proposal_sent',
              lockedByUid: user.uid,
              lockedByUsername: currentUserName,
              lockReason: 'proposal',
              lockedAt: serverTimestamp(),
              expiresAt: new Date(Date.now() + 15 * 60 * 1000),
            },
          });
        } catch (err) {
          console.warn('Kon planningLock niet updaten', err);
        }
      }

      // 5. Clear planning_pending reminders for this connection
      if (connectionId) {
        try {
          const remindersQuery = query(
            collection(db, 'inboxItems'),
            where('type', '==', 'planning_pending'),
            where('connectionId', '==', connectionId)
          );
          const remindersSnapshot = await getDocs(remindersQuery);
          const deletePromises = remindersSnapshot.docs.map(d => 
            updateDoc(doc(db, 'inboxItems', d.id), {
              isRead: true,
              resolvedAt: serverTimestamp(),
            })
          );
          await Promise.all(deletePromises);
          console.log('[planning-lock] Cleared reminders after proposal sent');
        } catch (err) {
          console.warn('Kon reminders niet clearen', err);
        }
      }

      // 6. Release planning owner lock
      if (connectionId && user?.uid) {
        try {
          const { releasePlanningLock } = await import('../services/connectionJourneyService');
          await releasePlanningLock(connectionId, user.uid);
          console.log('[planning-lock] Released lock after proposal sent');
        } catch (err) {
          console.warn('Kon lock niet releasen', err);
        }
      }

      // Success: reset and show feedback
      const successMessage = 'Voorstel verzonden en aangekomen';
      showToast(successMessage, 'success');
      setProposalSlots([]);
      setProposedDates([{ date: '', time: '', endTime: '' }, { date: '', time: '', endTime: '' }]);
      
      // Dispatch global notification event for ConnectieFlow to show
      window.dispatchEvent(new CustomEvent('appointment-planned', {
        detail: {
          message: successMessage,
          type: 'success'
        }
      }));
      
      // Close agenda - both users stay in chat (no navigation to dashboard)
      setTimeout(() => {
        onClosePlanning?.();
        // Don't navigate - let users stay in chat to see the timer and continue conversation
      }, 3000);
    } catch (error) {
      console.error('Error sending proposal:', error);
      showToast('Fout bij verzenden voorstel', 'error');
      setProposalError('Verzenden mislukt');
    } finally {
      setProposalLoading(false);
    }
  };

  // Handle responding to incoming proposals
  const respondToProposal = async (proposal: MeetingProposal, accept: boolean, selectedIndex?: number) => {
    try {
      if (accept && selectedIndex !== undefined) {
        const dates = proposal.proposedDates || [];
        const selectedSlot = dates[selectedIndex];
        if (!selectedSlot) {
          showToast('Ongeldig tijdslot geselecteerd', 'error');
          return;
        }

        // Create the calendar event
        const startDateTime = new Date(`${selectedSlot.date}T${selectedSlot.time}`);
        const endDateTime = selectedSlot.endTime
          ? new Date(`${selectedSlot.date}T${selectedSlot.endTime}`)
          : new Date(startDateTime.getTime() + 30 * 60 * 1000);

        await addDoc(collection(db, 'calendarEvents'), {
          title: `Meeting met ${proposal.fromUserName}`,
          description: proposal.message || 'Geplande meeting via SuperKonnected',
          startTime: startDateTime,
          endTime: endDateTime,
          type: 'meeting',
          category: 'work',
          source: 'superkonnected',
          participants: [user?.uid, proposal.fromUserId],
          participantNames: [user?.displayName || '', proposal.fromUserName],
          status: 'confirmed',
          createdBy: user?.uid,
          createdAt: serverTimestamp(),
          reminderEnabled: true,
          reminderMinutesBefore: 15,
          reminderSent: false,
          proposalId: proposal.id,
          connectionId: proposal.connectionId || null
        });

        // Update proposal status
        await updateDoc(doc(db, 'meetingProposals', proposal.id), {
          status: 'accepted',
          respondedAt: serverTimestamp(),
          selectedDateIndex: selectedIndex
        });

        // Notify the original sender
        await addDoc(collection(db, 'notifications'), {
          userId: proposal.fromUserId,
          type: 'proposal_response',
          title: '✅ Voorstel geaccepteerd!',
          message: `${user?.displayName || 'Iemand'} heeft je voorstel geaccepteerd voor ${startDateTime.toLocaleDateString('nl-NL')} om ${startDateTime.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}.`,
          data: {
            proposalId: proposal.id,
            acceptedDate: startDateTime.toISOString(),
            connectionId: proposal.connectionId
          },
          read: false,
          createdAt: serverTimestamp()
        });

        // Create inbox item for sender
        await addDoc(collection(db, 'inboxItems'), {
          userId: proposal.fromUserId,
          type: 'proposal_accepted',
          category: 'planning',
          title: 'Voorstel geaccepteerd! 🎉',
          preview: `${user?.displayName || 'Connectie'} heeft gekozen voor ${startDateTime.toLocaleDateString('nl-NL')}`,
          fromUserId: user?.uid,
          fromUserName: user?.displayName || '',
          data: {
            proposalId: proposal.id,
            acceptedDate: startDateTime.toISOString()
          },
          read: false,
          createdAt: serverTimestamp()
        });

        showToast('Afspraak bevestigd en toegevoegd aan beide agenda\'s!', 'success');
      } else {
        // Decline
        await updateDoc(doc(db, 'meetingProposals', proposal.id), {
          status: 'declined',
          respondedAt: serverTimestamp()
        });

        // Notify sender
        await addDoc(collection(db, 'notifications'), {
          userId: proposal.fromUserId,
          type: 'proposal_response',
          title: '❌ Voorstel afgewezen',
          message: `${user?.displayName || 'Iemand'} kan helaas niet op de voorgestelde tijden.`,
          data: {
            proposalId: proposal.id,
            connectionId: proposal.connectionId
          },
          read: false,
          createdAt: serverTimestamp()
        });

        showToast('Voorstel afgewezen', 'info');
      }
    } catch (error) {
      console.error('Error responding to proposal:', error);
      showToast('Fout bij reageren op voorstel', 'error');
    }
  };

  // ============================================================================
  // COUNTER-PROPOSAL HANDLER
  // ============================================================================
  const startCounterProposal = (proposal: MeetingProposal) => {
    setCounterProposalMode(true);
    setCounterProposalTarget(proposal);
    setCounterSlots([]);
    setPlanningMode('proposal');
    setSidePanelOpen(true);
    showToast('Tegenvoorstel: klik op 3 tijdsblokken in de kalender', 'info');
  };

  const sendCounterProposal = async () => {
    if (!user?.uid || !counterProposalTarget || counterSlots.length < 2) {
      showToast('Kies minimaal 2 momenten voor je tegenvoorstel', 'info');
      return;
    }

    setProposalLoading(true);
    try {
      // Mark original proposal as countered
      await updateDoc(doc(db, 'meetingProposals', counterProposalTarget.id), {
        status: 'counter',
        respondedAt: serverTimestamp(),
      });

      // Create new counter-proposal
      const currentUserName = user.displayName || 'Connectie';
      const slotsPayload = counterSlots.map(s => ({ start: s.start, end: s.end }));
      const slotTexts = counterSlots.map(s =>
        formatRangeNl(new Date(s.start), new Date(s.end))
      );

      await addDoc(collection(db, 'meetingProposals'), {
        connectionId,
        fromUserId: user.uid,
        fromUserName: currentUserName,
        toUserId: counterProposalTarget.fromUserId,
        toUserName: counterProposalTarget.fromUserName,
        slots: slotsPayload,
        status: 'sent',
        isCounterProposal: true,
        originalProposalId: counterProposalTarget.id,
        createdAt: serverTimestamp(),
      });

      // Notify original proposer
      await addDoc(collection(db, 'notifications'), {
        userId: counterProposalTarget.fromUserId,
        type: 'proposal_response',
        title: 'Tegenvoorstel ontvangen',
        message: `${currentUserName} stelde andere momenten voor: ${slotTexts.join(' / ')}`,
        data: {
          connectionId,
          originalProposalId: counterProposalTarget.id,
        },
        read: false,
        createdAt: serverTimestamp(),
      });

      showToast('Tegenvoorstel verzonden', 'success');
      setCounterProposalMode(false);
      setCounterProposalTarget(null);
      setCounterSlots([]);
      setPlanningMode('none');
    } catch (error) {
      console.error('Error sending counter proposal:', error);
      showToast('Fout bij verzenden tegenvoorstel', 'error');
    } finally {
      setProposalLoading(false);
    }
  };

  // ============================================================================
  // TIME SLOT FAVORITES HANDLER
  // ============================================================================
  const toggleTimeSlotFavorite = async (start: string, end: string) => {
    if (!user?.uid || !connectionId) return;

    // Normalize to minute-precision UTC ISO string before storing.
    // This ensures that any two users starring the same logical time slot
    // always write identical strings, making match detection reliable.
    const normStart = new Date(Math.round(new Date(start).getTime() / 60_000) * 60_000).toISOString();
    const normEnd   = new Date(Math.round(new Date(end).getTime()   / 60_000) * 60_000).toISOString();

    // Find existing favorite using minute-rounded comparison
    const existing = timeSlotFavorites.find(f =>
      Math.round(new Date(f.start).getTime() / 60_000) === Math.round(new Date(normStart).getTime() / 60_000) &&
      Math.round(new Date(f.end).getTime()   / 60_000) === Math.round(new Date(normEnd).getTime()   / 60_000)
    );

    try {
      if (existing) {
        await deleteDoc(doc(db, 'connections', connectionId, 'timeSlotFavorites', existing.id));
        showToast('Favoriet verwijderd', 'info');
      } else {
        await addDoc(collection(db, 'connections', connectionId, 'timeSlotFavorites'), {
          userId: user.uid,
          userName: user.displayName || 'Connectie',
          start: normStart,
          end:   normEnd,
          createdAt: serverTimestamp(),
        });
        showToast('Tijdslot als favoriet gemarkeerd', 'success');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showToast('Fout bij opslaan favoriet', 'error');
    }
  };

  // toMinuteMs is defined at module level (top of file) to avoid duplication.

  const isFavorite = useCallback((start: string, end: string) => {
    const s = toMinuteMs(start);
    const e = toMinuteMs(end);
    return timeSlotFavorites.some(f => toMinuteMs(f.start) === s && toMinuteMs(f.end) === e);
  }, [timeSlotFavorites]);

  const isOtherUserFavorite = useCallback((start: string, end: string) => {
    const s = toMinuteMs(start);
    const e = toMinuteMs(end);
    return otherUserFavorites.some(f => toMinuteMs(f.start) === s && toMinuteMs(f.end) === e);
  }, [otherUserFavorites]);

  // ============================================================================
  // NOTIFICATION HANDLERS
  // ============================================================================
  const markNotificationAsRead = async (notificationId: string) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), { read: true });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllNotificationsAsRead = async () => {
    try {
      const unreadNotifs = notifications.filter(n => !n.read);
      await Promise.all(
        unreadNotifs.map(n => updateDoc(doc(db, 'notifications', n.id), { read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // ============================================================================
  // RENDER - Loading State
  // ============================================================================
  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-lg max-w-7xl mx-auto overflow-hidden">
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Agenda</h2>
          <p className="text-gray-600">Je moet ingelogd zijn om je agenda te bekijken.</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDER - Main Component
  // ============================================================================
  return (
    <div className="bg-white rounded-lg shadow-lg max-w-7xl mx-auto relative flex flex-col" style={{ height: '90vh', maxHeight: '900px' }}>
      {/* Floating hover time indicator */}
      {gridHover && (
        <div
          className="fixed z-[9999] pointer-events-none px-2 py-0.5 rounded-md text-xs font-semibold text-white shadow-md"
          style={{
            left: gridHover.x + 14,
            top: gridHover.y - 10,
            background: 'rgba(30, 41, 59, 0.85)',
            backdropFilter: 'blur(4px)',
          }}
        >
          {gridHover.label}
        </div>
      )}

      {indexWarnings.length > 0 && (
        <div className="bg-stone-50 border-l-4 border-[#7A9488] text-[#4A5A3D] px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div className="space-y-1 text-sm">
              <div className="font-semibold">Firestore index ontbreekt</div>
              {indexWarnings.map((warning) => (
                <div key={warning.key} className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  <div>
                    <div>{warning.text}</div>
                    {warning.link && (
                      <a
                        href={warning.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#4F6660] underline"
                      >
                        Open index in Firebase Console
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* ── HEADER (sticky) ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-4 pt-3 pb-2 flex-shrink-0 sticky top-0 z-20">

        {/* Row 1: Title + Connection Selector + controls */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* Connection Selector Pill */}
            <div className="min-w-0 flex-1" ref={connectionPickerRef}>
              <button
                ref={pickerButtonRef}
                onClick={() => {
                  if (!showConnectionPicker && pickerButtonRef.current) {
                    const r = pickerButtonRef.current.getBoundingClientRect();
                    setPickerDropPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 288) });
                  }
                  setShowConnectionPicker(v => !v);
                }}
                className={`flex items-center gap-2 w-full max-w-xs px-2.5 py-1.5 rounded-xl border transition-all text-left ${
                  activeConnectionData
                    ? 'bg-white border-gray-200 hover:border-[#7A9488]'
                    : 'bg-amber-50 border-amber-300 hover:border-amber-400 animate-pulse'
                }`}
                title="Kies met wie je wil plannen"
              >
                {/* Avatar */}
                {activeConnectionData ? (
                  <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #e5e7eb' }}>
                    {otherUserPhoto ? (
                      <img src={otherUserPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', lineHeight: 1 }}>
                        {otherUserName.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    )}
                  </div>
                ) : (
                  <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserIcon size={13} className="text-amber-700" />
                  </div>
                )}
                {/* Name + context */}
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-gray-800 leading-tight truncate">
                    {activeConnectionData ? otherUserName : 'Kies connectie…'}
                  </div>
                  <div className="text-[10px] text-gray-400 leading-none">
                    {activeConnectionData ? 'Plannen voor' : 'Niemand geselecteerd'}
                  </div>
                </div>
                {/* Chevron */}
                <ChevronRight
                  size={13}
                  className={`flex-shrink-0 text-gray-400 transition-transform ${showConnectionPicker ? 'rotate-90' : ''}`}
                />
              </button>

              {/* Legend dots (compact, inline) */}
              {activeConnectionData && !showConnectionPicker && (
                <div className="flex items-center gap-3 mt-1 pl-1">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span className="text-[10px] text-gray-400">Jij</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-[10px] text-gray-400">{otherUserName}</span>
                  </div>
                  {isPlanningOwner && (
                    <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                      Jij plant nu
                    </span>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Right controls: bell, sync, view toggle */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors relative"
                title="Notificaties"
              >
                <Bell size={17} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>

            {!googleCalendarStatus.connected ? (
              <button
                onClick={handleGoogleCalendarConnect}
                className="px-2.5 py-1.5 bg-orange-50 border border-orange-200 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors text-xs font-medium"
                title="Verbind Google Calendar"
              >
                Verbind
              </button>
            ) : (
              <button
                onClick={handleGoogleCalendarSync}
                disabled={googleCalendarStatus.syncing}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
                title={googleCalendarStatus.syncing ? 'Bezig...' : 'Sync Google Calendar'}
              >
                <RefreshCw size={15} className={googleCalendarStatus.syncing ? 'animate-spin' : ''} />
              </button>
            )}

            <button
              onClick={() => setViewMode(viewMode === 'week' ? 'biweek' : 'week')}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              title="Wissel weekweergave"
            >
              {viewMode === 'week' ? '2 wk' : '1 wk'}
            </button>
          </div>
        </div>

        {/* Row 2: Navigation + planning mode tabs */}
        <div className="flex items-center justify-between mt-2 gap-2">
          {/* Week navigation — styled like AgendaPage */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => navigateWeek(-1)}
              className="p-1.5 rounded-full hover:bg-stone-100 text-gray-600 transition-colors border border-gray-200"
              title="Vorige week"
            >
              <ChevronLeft size={16} />
            </button>
            {/* Week range label: "16 – 22 mrt 2026" */}
            <span className="text-sm font-semibold text-gray-800 min-w-[130px] text-center select-none">
              {(() => {
                const start = weekDates[0];
                const end = weekDates[weekDates.length - 1];
                if (!start || !end) return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
                const sameMonth = start.getMonth() === end.getMonth();
                const sameYear = start.getFullYear() === end.getFullYear();
                const startLabel = sameMonth
                  ? `${start.getDate()}`
                  : `${start.getDate()} ${start.toLocaleDateString('nl-NL', { month: 'short' })}`;
                const endLabel = `${end.getDate()} ${end.toLocaleDateString('nl-NL', { month: 'short' })}${!sameYear ? ` ${end.getFullYear()}` : ''}`;
                return `${startLabel} – ${endLabel} ${sameYear ? end.getFullYear() : ''}`;
              })()}
            </span>
            <button
              onClick={() => navigateWeek(1)}
              className="p-1.5 rounded-full hover:bg-stone-100 text-gray-600 transition-colors border border-gray-200"
              title="Volgende week"
            >
              <ChevronRight size={16} />
            </button>
            {/* Vandaag button — jump back to current week */}
            {!isToday(weekDates[0] ?? new Date()) && !weekDates.some(d => isToday(d)) && (
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-2.5 py-1 text-xs font-semibold text-[#4F6660] bg-stone-100 hover:bg-stone-200 rounded-lg transition-colors border border-stone-200"
                title="Ga naar deze week"
              >
                Vandaag
              </button>
            )}
          </div>

          {/* Planning mode pill-tabs */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
            {/* Handmatig (mutual proposal) — main planning mode */}
            <button
              onClick={() => {
                setPlanningMode('proposal');
                setSidePanelOpen(true);
                setProposalError(null);
                showToast('Klik op 1–3 tijdsblokken in de kalender. Markeer blokken van je connectie als favoriet om een match te maken!', 'info');
              }}
              title="Handmatig plannen — kies blokken samen"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                planningMode === 'proposal'
                  ? 'bg-white text-orange-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Calendar size={13} />
              Handmatig
            </button>

            {/* Autoplan */}
            <button
              onClick={handleAutoplan}
              disabled={isAutoplanLoading || autoplanInFlight.current || Boolean(isObserver)}
              title="Automatisch plannen"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                planningMode === 'autoplan'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              } ${isObserver ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              {(isAutoplanLoading || autoplanInFlight.current) && (
                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
              )}
              {!(isAutoplanLoading || autoplanInFlight.current) && (
                <RefreshCw size={13} />
              )}
              Autoplan
            </button>

            {/* Availability */}
            <button
              onClick={() => {
                const next = !showAvailabilityOnly;
                setShowAvailabilityOnly(next);
                if (next) {
                  setPlanningMode('availabilityOnly');
                  setSidePanelOpen(true);
                } else {
                  setPlanningMode('none');
                  setSidePanelOpen(false);
                }
              }}
              title="Beschikbaarheid bekijken"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                planningMode === 'availabilityOnly'
                  ? 'bg-white text-emerald-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Check size={13} />
              Beschikbaar
            </button>
          </div>

          {/* Incoming proposals badge */}
          {incomingProposals.length > 0 && (
            <button
              onClick={() => { setSidePanelOpen(true); setPlanningMode('none'); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors"
            >
              <Bell size={13} />
              {incomingProposals.length} voorstel{incomingProposals.length > 1 ? 'len' : ''}
            </button>
          )}
        </div>

        {/* Permanent planning mode selector — two big buttons always visible when planning with a connection */}
        {otherUserId && (
          <div
            style={{
              marginTop: 8,
              background: 'linear-gradient(135deg, #f0f7f5 0%, #eaf3f0 100%)',
              border: '1.5px solid #c0d8d0',
              borderRadius: 14,
              padding: '12px 12px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#4a7a6a', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
              Hoe wil je plannen?
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              {/* Handmatig button */}
              <button
                onClick={() => {
                  setPlanningMode('proposal');
                  setSidePanelOpen(true);
                  setProposalError(null);
                }}
                style={{
                  flex: 1,
                  background: planningMode === 'proposal'
                    ? 'linear-gradient(135deg, #7A9488 0%, #4F6660 100%)'
                    : '#fff',
                  border: planningMode === 'proposal' ? 'none' : '1.5px solid #c8ddd8',
                  borderRadius: 10,
                  padding: '11px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxShadow: planningMode === 'proposal' ? '0 4px 12px rgba(92,107,74,0.25)' : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={planningMode === 'proposal' ? '#fff' : '#4F6660'} strokeWidth={2.2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  <span style={{ fontSize: 13, fontWeight: 700, color: planningMode === 'proposal' ? '#fff' : '#3a5a3a' }}>Handmatig</span>
                </div>
                <span style={{ fontSize: 11, color: planningMode === 'proposal' ? 'rgba(255,255,255,0.8)' : '#6a8a7a', lineHeight: 1.35 }}>
                  {planningMode === 'proposal'
                    ? `${proposalSlots.length}/3 tijdsvakken geselecteerd`
                    : 'Klik op tijdsvakken in de kalender'}
                </span>
              </button>

              {/* Autoplan button */}
              <button
                onClick={handleAutoplan}
                disabled={isAutoplanLoading || Boolean(isObserver)}
                style={{
                  flex: 1,
                  background: planningMode === 'autoplan' && autoplanState.results.length > 0
                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                    : planningMode === 'autoplan' && !isAutoplanLoading && autoplanState.results.length === 0
                    ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                    : isAutoplanLoading
                    ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: 10,
                  padding: '11px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  cursor: isAutoplanLoading || isObserver ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  boxShadow: planningMode === 'autoplan' && autoplanState.results.length > 0
                    ? '0 4px 12px rgba(22,163,74,0.35)'
                    : '0 4px 12px rgba(37,99,235,0.3)',
                  opacity: isObserver ? 0.5 : 1,
                  transition: 'all 0.25s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isAutoplanLoading
                    ? <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                    : planningMode === 'autoplan' && autoplanState.results.length > 0
                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                    : planningMode === 'autoplan' && autoplanState.results.length === 0 && !isAutoplanLoading
                    ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="12" r="10"/></svg>
                    : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.2}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  }
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                    {isAutoplanLoading ? 'Zoeken…'
                      : planningMode === 'autoplan' && autoplanState.results.length > 0 ? `${autoplanState.results.length} match${autoplanState.results.length > 1 ? 'es' : ''}!`
                      : planningMode === 'autoplan' && autoplanState.results.length === 0 ? 'Geen match'
                      : 'Autoplan'}
                  </span>
                </div>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.82)', lineHeight: 1.35 }}>
                  {isAutoplanLoading ? 'Overlappende momenten zoeken…'
                    : planningMode === 'autoplan' && autoplanState.results.length > 0 ? 'Klik "Plan afspraak" in het paneel →'
                    : planningMode === 'autoplan' && autoplanState.results.length === 0 ? 'Voeg meer beschikbaarheid toe'
                    : 'Automatisch beste tijd vinden'}
                </span>
              </button>
            </div>

            {/* Active mode hint */}
            {planningMode === 'proposal' && (
              <div style={{ fontSize: 11, color: '#5a7a6a', display: 'flex', alignItems: 'center', gap: 5 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#7A9488" strokeWidth={2.5}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                Selecteer 2–3 tijdsvakken in de kalender, dan verstuur je het voorstel
              </div>
            )}
            {planningMode === 'autoplan' && !isAutoplanLoading && autoplanState.results.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', border: '1.5px solid #86efac', borderRadius: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>
                    {autoplanState.results.length} match{autoplanState.results.length > 1 ? 'es' : ''} gevonden!
                  </span>
                  <span style={{ fontSize: 11, color: '#166534', marginLeft: 4 }}>Zie het paneel rechts →</span>
                </div>
              </div>
            )}
            {planningMode === 'autoplan' && !isAutoplanLoading && autoplanState.results.length === 0 && !autoplanState.error && autoplanState.results !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/><circle cx="12" cy="12" r="10"/></svg>
                </div>
                <span style={{ fontSize: 12, color: '#c2410c', fontWeight: 600 }}>Geen overlapping gevonden — voeg meer beschikbaarheid toe of plan handmatig</span>
              </div>
            )}
          </div>
        )}

        {/* Row 3: Context hint for non-connection modes */}
        {planningMode !== 'none' && planningMode !== 'proposal' && planningMode !== 'autoplan' && (
          <div className="flex items-center justify-between mt-2 bg-orange-50 border border-orange-100 rounded-lg px-3 py-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-orange-800 font-medium">
                {planningMode === 'manual'   && '👆 Klik op de kalender om een tijdslot te kiezen'}
                {planningMode === 'availabilityOnly' && '👁 Alleen beschikbare momenten worden getoond'}
              </span>
            </div>
            <button
              onClick={resetPlanning}
              className="ml-4 text-xs text-orange-600 hover:text-orange-800 font-semibold flex-shrink-0 hover:underline"
            >
              Stop
            </button>
          </div>
        )}

        {/* Confirmed meeting location banner */}
        {localConfirmedLocation && (
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              border: '1.5px solid #86efac',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(22,163,74,0.08)',
            }}
          >
            <div style={{ width: 32, height: 32, borderRadius: 10, background: '#dcfce7', border: '1.5px solid #86efac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: '#15803d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Afgesproken locatie</p>
              <p style={{ margin: '1px 0 0', fontSize: 13, fontWeight: 700, color: '#14532d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {localConfirmedLocation.name !== localConfirmedLocation.address ? localConfirmedLocation.name : localConfirmedLocation.address}
              </p>
              {localConfirmedLocation.name !== localConfirmedLocation.address && localConfirmedLocation.address && (
                <p style={{ margin: 0, fontSize: 11, color: '#16a34a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{localConfirmedLocation.address}</p>
              )}
            </div>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: '#16a34a' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
        )}

        {/* Planning error */}
        {planningError && (
          <div className="mt-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
            ⚠️ {planningError}
          </div>
        )}

        {/* Google reconnect */}
        {googleReconnectNeeded && (
          <div className="mt-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center justify-between">
            <span className="text-xs">
              {googleReconnectNeeded === 'INVALID_GOOGLE_CLIENT'
                ? 'Google Calendar configuratie ongeldig.'
                : 'Google Calendar toegang verlopen.'}
            </span>
            <button
              onClick={handleGoogleCalendarConnect}
              className="ml-3 px-3 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700"
            >
              Opnieuw koppelen
            </button>
          </div>
        )}

        {/* Planning Lock - Observer Mode */}
        {isObserver && livePlanningLock?.ownerName && (
          <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <UserIcon size={15} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs text-gray-600">
                <span className="font-semibold text-gray-700">{livePlanningLock.ownerName}</span> is aan het plannen — wacht even of doe alvast een voorstel
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={async () => {
                  if (onClosePlanning && propConnectionId && currentUserId && livePlanningLock.ownerId) {
                    try {
                      await addDoc(collection(db, 'inboxItems'), {
                        userId: currentUserId,
                        type: 'planning_pending',
                        title: 'Planning nog niet afgerond',
                        message: `Planning met ${livePlanningLock.ownerName} nog niet afgerond`,
                        connectionId: propConnectionId,
                        planningOwnerId: livePlanningLock.ownerId,
                        idempotencyKey: `planning_pending:${propConnectionId}:${livePlanningLock.ownerId}`,
                        isRead: false,
                        createdAt: serverTimestamp(),
                      });
                    } catch (error) {
                      console.error('[planning-lock] Failed to create reminder:', error);
                    }
                  }
                  onClosePlanning?.();
                }}
                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Sluiten
              </button>
              <button
                onClick={() => {
                  setPlanningMode('proposal');
                  setSidePanelOpen(true);
                  showToast('Voorstel doen - selecteer momenten', 'info');
                }}
                className="px-3 py-1 text-xs bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                Voorstel doen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* NOTIFICATIONS DROPDOWN */}
      {showNotifications && (
        <div className="absolute top-20 right-4 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-96 overflow-hidden">
          <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <h3 className="font-semibold text-gray-800">Notificaties</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllNotificationsAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Alles gelezen
                </button>
              )}
              <button onClick={() => setShowNotifications(false)}>
                <X size={16} className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            </div>

          <div className="max-h-72 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                Geen notificaties
                      </div>
                    ) : (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => markNotificationAsRead(notif.id)}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !notif.read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-800 truncate">{notif.title}</div>
                      <div className="text-xs text-gray-600 mt-1 line-clamp-2">{notif.message}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {notif.createdAt.toLocaleString('nl-NL', {
                                  day: 'numeric',
                                  month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                              </div>
                            </div>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></div>
                    )}
                          </div>
                        </div>
                      ))
                    )}
          </div>
                  </div>
                )}

      {/* INCOMING PROPOSALS — compact collapsible bar, does NOT block the calendar */}
      {incomingProposals.length > 0 && (
        <div className="border-b border-yellow-200 bg-yellow-50 flex-shrink-0">
          {/* Collapsed header — always visible */}
          <button
            className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold text-yellow-900 hover:bg-yellow-100 transition-colors"
            onClick={() => setSidePanelOpen(prev => !prev) /* reuse panel toggle to show proposals */}
          >
            <span className="flex items-center gap-2">
              <Bell size={15} />
              {incomingProposals.length} openstaand{incomingProposals.length > 1 ? 'e voorstellen' : ' voorstel'} — klik op de knop rechts om te bekijken
            </span>
            <button
              className="px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-semibold rounded hover:bg-yellow-500 transition-colors"
              onClick={(e) => { e.stopPropagation(); setSidePanelOpen(true); setPlanningMode('none'); }}
            >
              Bekijk
            </button>
          </button>

          {/* Proposals list — shown in side panel instead (see below) */}
        </div>
      )}

      {/* INCOMING PROPOSALS SIDE PANEL CONTENT — injected when panel has no mode */}
      {false && incomingProposals.length > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4 rounded-r-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-yellow-900 flex items-center gap-2">
              <Bell size={20} />
              Openstaande Voorstellen ({incomingProposals.length})
            </h3>
          </div>
          <div className="space-y-3">
            {incomingProposals.map((proposal) => (
              <div key={proposal.id} className="bg-white p-4 rounded-lg border border-yellow-200 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <UserIcon size={16} className="text-gray-400" />
                      Voorstel van {proposal.fromUserName}
                    </div>
                    {proposal.message && (
                      <div className="text-sm text-gray-600 mt-1 italic">"{proposal.message}"</div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      {proposal.createdAt.toLocaleString('nl-NL')}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  {(proposal.proposedDates || []).map((slot, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between bg-gray-50 p-2 rounded hover:bg-gray-100 transition-colors"
                    >
                      <div className="text-sm">
                        <span className="font-medium">
                          {new Date(slot.date).toLocaleDateString('nl-NL', {
                                weekday: 'short',
                                day: 'numeric',
                            month: 'long'
                          })}
                        </span>
                        <span className="text-gray-600 ml-2">
                          {slot.time}{slot.endTime && ` - ${slot.endTime}`}
                        </span>
                            </div>
                      <button
                        onClick={() => respondToProposal(proposal, true, idx)}
                        className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors flex items-center gap-1"
                      >
                        <Check size={14} />
                        Kies
                      </button>
                          </div>
                  ))}
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
                          <button
                    onClick={() => startCounterProposal(proposal)}
                    className="px-3 py-1 text-blue-600 text-sm hover:bg-blue-50 rounded transition-colors flex items-center gap-1"
                          >
                    <RefreshCw size={14} />
                    Tegenvoorstel
                          </button>
                          <button
                    onClick={() => respondToProposal(proposal, false)}
                    className="px-3 py-1 text-red-600 text-sm hover:bg-red-50 rounded transition-colors"
                          >
                    Kan niet op deze tijden
                          </button>
                        </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* END incoming proposals (hidden block) */}

      {/* AVAILABILITY SUGGESTIONS */}
      {selectedConnectionData?.availabilitySuggestions &&
        selectedConnectionData.availabilitySuggestions.length > 0 &&
        !hideAvailabilitySuggestions && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 m-4 rounded-r-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-blue-900">
                Automatische Planningsvoorstellen
              </h3>
              <button
                onClick={() => setHideAvailabilitySuggestions(true)}
                className="text-blue-600 hover:text-blue-800 p-1"
              >
                <X size={20} />
              </button>
                        </div>
            <p className="text-sm text-blue-700 mb-3">
              {selectedConnectionData.availabilitySuggestions.length} overlappende momenten gevonden
            </p>
            <div className="space-y-2">
              {selectedConnectionData.availabilitySuggestions.slice(0, 5).map((suggestion: any, index: number) => (
                <div
                  key={index}
                  className="bg-white p-3 rounded-lg border border-blue-200 flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {suggestion.start.toLocaleDateString('nl-NL', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long'
                      })}
                    </div>
                    <div className="text-sm text-gray-600">
                      {suggestion.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} -{' '}
                      {suggestion.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                        </div>
                        <button
                    onClick={() => {
                      setProposedDates([{
                        date: suggestion.start.toISOString().split('T')[0],
                        time: suggestion.start.toTimeString().slice(0, 5),
                        endTime: suggestion.end.toTimeString().slice(0, 5)
                      }]);
                      setShowProposalModal(true);
                    }}
                    className="px-3 py-1 rounded text-sm bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  >
                    Selecteer
                        </button>
                      </div>
              ))}
                    </div>
                  </div>
                )}

      {/* CALENDAR GRID + SIDE PANEL */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main Calendar Area */}
        <div className="flex flex-col flex-1 overflow-hidden border-r border-gray-200">
          {/* Calendar layer toggles */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100 flex-shrink-0 flex-wrap">
            {planningMode !== 'none' ? (
              // Planning mode: show toggles, default off
              <>
                <span className="text-[10px] text-gray-400 font-medium mr-1">Toon:</span>
                {/* Toggle: Jouw agenda */}
                <button
                  onClick={() => setShowOwnAgenda(v => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all ${
                    showOwnAgenda
                      ? 'bg-orange-100 border-orange-300 text-orange-700'
                      : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${showOwnAgenda ? 'bg-orange-400' : 'bg-gray-200'}`} />
                  Jouw agenda
                  <span className={`text-[9px] font-bold ${showOwnAgenda ? 'text-orange-500' : 'text-gray-300'}`}>
                    {showOwnAgenda ? 'AAN' : 'UIT'}
                  </span>
                </button>

                {/* Toggle: Vrij voor netwerken */}
                <button
                  onClick={() => setShowAvailabilityLayer(v => !v)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-all ${
                    showAvailabilityLayer
                      ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                      : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-2.5 h-2.5 rounded-sm flex-shrink-0 ${showAvailabilityLayer ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                  Vrij voor netwerken
                  <span className={`text-[9px] font-bold ${showAvailabilityLayer ? 'text-emerald-500' : 'text-gray-300'}`}>
                    {showAvailabilityLayer ? 'AAN' : 'UIT'}
                  </span>
                </button>

                {/* Always-on: proposal blocks legend */}
                <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-200">
                  <div className="w-2.5 h-2.5 rounded-sm bg-orange-300 border border-orange-400 flex-shrink-0" />
                  <span className="text-[11px] text-gray-500">Jij</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm bg-indigo-200 border border-indigo-400 flex-shrink-0" />
                  <span className="text-[11px] text-gray-500">{otherUserName || 'Connectie'}</span>
                </div>
              </>
            ) : (
              // Normal mode: static legend
              <>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-orange-100 border border-orange-300 flex-shrink-0" />
                  <span className="text-xs text-gray-500">Jouw agenda</span>
                </div>
                {activeConnectionData?.name && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300 flex-shrink-0" />
                    <span className="text-xs text-gray-500">{otherUserName}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 flex-shrink-0" />
                  <span className="text-xs text-gray-500">Vrij voor netwerken</span>
                </div>
              </>
            )}
          </div>

          {/* Days Header — styled like AgendaPage */}
          <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
            {weekDates.map((date, index) => {
              const today = isToday(date);
              return (
                <div
                  key={index}
                  className={`flex-1 py-2 px-1 text-center border-r border-gray-200 last:border-r-0 transition-colors ${today ? 'bg-stone-50' : ''}`}
                >
                  <div className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">
                    {daysOfWeek[date.getDay() === 0 ? 6 : date.getDay() - 1]}
                  </div>
                  <div className={`text-sm font-semibold mt-0.5 ${
                    today
                      ? 'w-7 h-7 rounded-full bg-[#7A9488] text-white mx-auto flex items-center justify-center'
                      : 'text-gray-800'
                  }`}>
                    {date.getDate()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time + Events Grid (scrollable) */}
          <div className="flex flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
            {/* Time Sidebar — styled like AgendaPage */}
            <div className="w-14 bg-stone-50 border-r border-gray-200 flex-shrink-0">
              <div className="min-h-[1200px]">
                {timeSlots.map((time, index) => (
                  <div
                    key={index}
                    className="h-[25px] flex items-start justify-end pr-2 pt-0.5 border-b border-gray-100"
                  >
                    <span className="text-[10px] text-gray-400 font-medium leading-none -mt-1">
                      {time}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar Grid — horizontal hour lines match AgendaPage */}
            <div className="flex-1 flex min-h-0">
              {weekDates.map((date, dayIndex) => (
                <div
                  key={dayIndex}
                  className="flex-1 border-r border-gray-200 last:border-r-0 relative min-h-[1200px] cursor-crosshair"
                  style={{
                    // Solid hour lines at 25px intervals, matching AgendaPage border-gray-200 (#E9E7E2)
                    backgroundImage: [
                      'repeating-linear-gradient(0deg, #E9E7E2, #E9E7E2 1px, transparent 1px, transparent 25px)',
                    ].join(','),
                    backgroundSize: '100% 25px',
                    backgroundPosition: '0 0',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const yOffset = e.clientY - rect.top;
                    const { hour, minute } = getTimeFromY(yOffset);
                    const roundedMinute = Math.round(minute / 15) * 15;
                    const displayMinute = roundedMinute === 60 ? 0 : roundedMinute;
                    const displayHour = roundedMinute === 60 ? hour + 1 : hour;
                    const label = `${String(displayHour).padStart(2,'0')}:${String(displayMinute).padStart(2,'0')}`;
                    setGridHover({ x: e.clientX, y: e.clientY, label });
                    setColumnHoverY({ dayIndex, y: yOffset });
                  }}
                  onMouseLeave={() => { setGridHover(null); setColumnHoverY(null); }}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const yOffset = e.clientY - rect.top;
                    handleGridClick(date, yOffset, e);
                  }}
                  onDoubleClick={(e) => {
                    // Double-click to toggle favorite
                    const rect = e.currentTarget.getBoundingClientRect();
                    const yOffset = e.clientY - rect.top;
                    const { hour, minute } = getTimeFromY(yOffset);
                    const startTime = new Date(date);
                    startTime.setHours(hour, minute, 0, 0);
                    const endTime = new Date(startTime);
                    endTime.setTime(startTime.getTime() + 60 * 60 * 1000);
                    toggleTimeSlotFavorite(startTime.toISOString(), endTime.toISOString());
                  }}
                >
                  {/* Horizontal hover guideline */}
                  {columnHoverY?.dayIndex === dayIndex && (
                    <div
                      className="absolute left-0 right-0 pointer-events-none z-10"
                      style={{ top: columnHoverY.y, height: '1px', background: 'rgba(59,130,246,0.5)' }}
                    />
                  )}

                  {getEventsForDate(date).map((event) => {
                    const [sh] = getTimeParts(event.startTime);
                    const [eh, em] = getTimeParts(event.endTime);
                    const [, sm] = getTimeParts(event.startTime);
                    const top = (sh - 6) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 25);

                    const meta = getEventDisplayMeta(event);
                    const isConnectie = isConnectieFlowEvent(event);
                    const isAvailability = isNetworkingAvailabilityEvent(event);
                    const isFilled = isNetworkingFilledEvent(event);

                    // Determine colors based on event type
                    let bgColor = meta.color;
                    let borderColor = meta.borderColor;
                    let textColor = 'text-gray-800';
                    let borderLeft = undefined;

                    if (isAvailability) {
                      // Green for availability slots
                      bgColor = '#D1FAE5'; // green-100
                      borderColor = '#34D399'; // green-400
                      textColor = 'text-green-900';
                      borderLeft = '3px solid #10B981'; // green-500
                    } else if (isFilled) {
                      // Purple for filled networking appointments
                      bgColor = '#EDE9FE'; // purple-100
                      borderColor = '#C4B5FD'; // purple-300
                      textColor = 'text-purple-900';
                      borderLeft = '3px solid #7C3AED'; // purple-600
                    } else if (isConnectie) {
                      // Other connectie flow events (fallback)
                      bgColor = meta.color;
                      borderColor = meta.borderColor;
                      textColor = 'text-purple-900';
                      borderLeft = '3px solid #7C3AED';
                    }

                    const handleEventClick = () => {
                      if (isAvailability) {
                        // Click handler for green availability slots
                        if (planningMode === 'manual' || planningMode === 'availabilityOnly') {
                          // Set selection to this slot's range
                          setManualSelection({ start: event.startTime, end: event.endTime });
                          showToast(`Geselecteerd: ${formatRangeNl(event.startTime, event.endTime)}`, 'info');
                        } else if (planningMode === 'proposal') {
                          // Add slot to proposalSlots
                          const newSlot = {
                            start: event.startTime.toISOString(),
                            end: event.endTime.toISOString(),
                          };
                          // Dedupe by start-end key
                          const key = `${newSlot.start}-${newSlot.end}`;
                          const existingKeys = proposalSlots.map(s => `${s.start}-${s.end}`);
                          if (!existingKeys.includes(key)) {
                            if (proposalSlots.length >= 3) {
                              showToast('Maximaal 3 slots toegestaan', 'info');
                              return;
                            }
                            const updated = [...proposalSlots, newSlot];
                            setProposalSlots(updated);
                            setProposedDates(updated.map(s => ({
                              date: s.start.split('T')[0],
                              time: s.start.split('T')[1].slice(0,5),
                              endTime: s.end.split('T')[1].slice(0,5),
                            })));
                            showToast(`Slot toegevoegd (${updated.length}/3)`, 'success');
                          } else {
                            showToast('Dit slot is al geselecteerd', 'info');
                          }
                        }
                      } else if (isConnectie) {
                        openConnectionFlow(event.connectionId || connectionId);
                      }
                    };

                              return (
                                <div
                                  key={event.id}
                        className={`absolute left-0.5 right-0.5 p-1 rounded shadow-sm transition-all duration-200 text-xs ${
                          hoveredEvent === event.id ? 'shadow-lg scale-[1.02] z-20' : 'z-10'
                        } ${textColor} ${
                          planningMode === 'proposal' && !isAvailability ? 'opacity-50 pointer-events-none' : 'cursor-pointer'
                        }`}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          backgroundColor: bgColor,
                          border: `1px solid ${borderColor}`,
                          borderLeft: borderLeft
                        }}
                        onMouseEnter={() => setHoveredEvent(event.id)}
                        onMouseLeave={() => setHoveredEvent(null)}
                        onClick={handleEventClick}
                        aria-label={meta.displayTitle}
                        title={meta.displayTitle}
                      >
                        <div className="font-semibold truncate">{meta.displayTitle}</div>
                        {isConnectie && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded-full text-[10px] font-semibold">
                              ConnectieFlow
                            </span>
                            {meta.subtitle && <span className="text-[10px] text-purple-800 truncate">{meta.subtitle}</span>}
                                    </div>
                        )}
                        {!isConnectie && hoveredEvent === event.id && height > 40 && (
                          <div className="mt-1 flex gap-1">
                            <button
                              onClick={() => setEditingEvent(event)}
                              className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-[9px]"
                            >
                              Bewerk
                            </button>
                            <button
                              onClick={() => {
                                if (event.recurring && event.recurring.frequency) {
                                  setEventToDelete(event);
                                  setShowDeleteModal(true);
                                  return;
                                }
                                deleteEvent(event.id, 'series');
                              }}
                              className="px-1.5 py-0.5 bg-red-500 text-white rounded text-[9px]"
                            >
                              Verwijder
                            </button>
                                    </div>
                        )}
                                </div>
                              );
                            })}

                  {/* Pending proposal overlays on calendar */}
                  {(sentProposals || []).flatMap(p => Array.isArray(p.slots) ? p.slots : []).map((slot, idx) => {
                    if (!slot?.start || !slot?.end) return null;
                    const slotStart = new Date(slot.start);
                    const slotEnd = new Date(slot.end);
                    if (slotStart.toDateString() !== date.toDateString()) return null;
                    const sh = slotStart.getHours();
                    const sm = slotStart.getMinutes();
                    const eh = slotEnd.getHours();
                    const em = slotEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    return (
                      <div
                        key={`proposal-${idx}`}
                        className="absolute left-0.5 right-0.5 z-5 pointer-events-none"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          background: 'rgba(251, 191, 36, 0.15)',
                          border: '1px dashed #F59E0B',
                          borderRadius: '4px',
                        }}
                      >
                        <span className="text-[9px] text-[#7A9488] font-medium px-1">
                          Voorstel uitstaand
                        </span>
                      </div>
                    );
                  })}

                  {/* Own proposal slots on the calendar grid */}
                  {(planningMode === 'proposal' ? proposalSlots : []).map((slot, idx) => {
                    if (!slot?.start || !slot?.end) return null;
                    const slotStart = new Date(slot.start);
                    const slotEnd = new Date(slot.end);
                    if (
                      slotStart.getFullYear() !== date.getFullYear() ||
                      slotStart.getMonth() !== date.getMonth() ||
                      slotStart.getDate() !== date.getDate()
                    ) return null;
                    const sh = slotStart.getHours();
                    const sm = slotStart.getMinutes();
                    const eh = slotEnd.getHours();
                    const em = slotEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    // My own block: show star if I favorited it; show other-star if the other user favorited it
                    const iStar   = isFavorite(slot.start, slot.end);
                    const theyStar = isOtherUserFavorite(slot.start, slot.end);
                    return (
                      <div
                        key={`own-proposal-${idx}`}
                        className="absolute left-0.5 right-0.5 pointer-events-none"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          zIndex: 6,
                          background: theyStar ? 'rgba(34,197,94,0.18)' : 'rgba(251, 146, 60, 0.18)',
                          border: `2px solid ${theyStar ? '#22c55e' : '#F97316'}`,
                          borderRadius: '4px',
                        }}
                        title={theyStar ? `${otherUserName || 'Connectie'} heeft dit blok gefavoriet` : 'Jouw voorstelblok'}
                      >
                        <div className="flex items-center gap-0.5 px-1">
                          {iStar && <Star size={8} fill="#F97316" color="#F97316" />}
                          {theyStar && <Star size={8} fill="#22c55e" color="#22c55e" />}
                          <span className="text-[9px] font-semibold" style={{ color: theyStar ? '#15803d' : '#c2410c' }}>
                            Jij
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Other user's live proposal slots (from connection document) */}
                  {(otherUserLiveSlots || []).map((slot, idx) => {
                    if (!slot?.start || !slot?.end) return null;
                    const slotStart = new Date(slot.start);
                    const slotEnd = new Date(slot.end);
                    if (
                      slotStart.getFullYear() !== date.getFullYear() ||
                      slotStart.getMonth() !== date.getMonth() ||
                      slotStart.getDate() !== date.getDate()
                    ) return null;
                    const sh = slotStart.getHours();
                    const sm = slotStart.getMinutes();
                    const eh = slotEnd.getHours();
                    const em = slotEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    // Their block: show star if I favorited it; show other-star if they favorited their own block
                    const iStar    = isFavorite(slot.start, slot.end);
                    const theyStar = isOtherUserFavorite(slot.start, slot.end);
                    const isMatch  = iStar && theyStar;
                    return (
                      <div
                        key={`other-live-${idx}`}
                        className="absolute left-0.5 right-0.5"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          zIndex: 6,
                          background: isMatch ? 'rgba(34,197,94,0.22)' : iStar ? 'rgba(16,185,129,0.18)' : 'rgba(99,102,241,0.15)',
                          border: `2px solid ${isMatch ? '#22c55e' : iStar ? '#10B981' : '#6366F1'}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => { e.stopPropagation(); toggleTimeSlotFavorite(slot.start, slot.end); }}
                        title={iStar ? 'Klik om favoriet te verwijderen' : 'Klik om als favoriet te markeren'}
                      >
                        <div className="flex items-center gap-0.5 px-1">
                          {/* My star on their block */}
                          {iStar && <Star size={8} fill={isMatch ? '#22c55e' : '#10B981'} color={isMatch ? '#22c55e' : '#10B981'} />}
                          {/* Their star on their own block */}
                          {theyStar && <Star size={8} fill="#6366F1" color="#6366F1" />}
                          <span className="text-[9px] font-semibold" style={{ color: isMatch ? '#15803d' : iStar ? '#047857' : '#4338CA' }}>
                            {otherUserName || 'Connectie'}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Counter-proposal slots (from other user's incoming proposals) */}
                  {(incomingProposals || []).flatMap(p =>
                    (Array.isArray(p.proposedDates) ? p.proposedDates : []).map((pd, pdIdx) => {
                      if (!pd?.date) return null;
                      const slotDate = new Date(pd.date);
                      if (slotDate.toDateString() !== date.toDateString()) return null;
                      const [sh, sm] = (pd.time || '09:00').split(':').map(Number);
                      const [eh, em] = (pd.endTime || '10:00').split(':').map(Number);
                      const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                      const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                      return (
                        <div
                          key={`incoming-${p.id}-${pdIdx}`}
                          className="absolute left-0.5 right-0.5 z-5 pointer-events-none"
                          style={{
                            top: `${top}px`,
                            height: `${height}px`,
                            background: 'rgba(234, 179, 8, 0.12)',
                            border: '1px dashed #7A9488',
                            borderRadius: '4px',
                          }}
                        >
                          <span className="text-[9px] text-[#4F6660] font-medium px-1">
                            Voorstel {p.fromUserName}
                          </span>
                        </div>
                      );
                    })
                  )}

                  {/* Favorite overlays */}
                  {(timeSlotFavorites || []).map((fav, idx) => {
                    if (!fav?.start || !fav?.end) return null;
                    const favStart = new Date(fav.start);
                    if (favStart.toDateString() !== date.toDateString()) return null;
                    const favEnd = new Date(fav.end);
                    const sh = favStart.getHours();
                    const sm = favStart.getMinutes();
                    const eh = favEnd.getHours();
                    const em = favEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    return (
                      <div
                        key={`fav-${idx}`}
                        className="absolute right-0 z-5 pointer-events-none"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          width: '4px',
                          background: '#F97316',
                          borderRadius: '2px',
                        }}
                        title="Jouw favoriet"
                      />
                    );
                  })}

                  {/* Other user favorite overlays */}
                  {(otherUserFavorites || []).map((fav, idx) => {
                    if (!fav?.start || !fav?.end) return null;
                    const favStart = new Date(fav.start);
                    if (favStart.toDateString() !== date.toDateString()) return null;
                    const favEnd = new Date(fav.end);
                    const sh = favStart.getHours();
                    const sm = favStart.getMinutes();
                    const eh = favEnd.getHours();
                    const em = favEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    return (
                      <div
                        key={`other-fav-${idx}`}
                        className="absolute left-0 z-5 pointer-events-none"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          width: '4px',
                          background: '#3B82F6',
                          borderRadius: '2px',
                        }}
                        title={`Favoriet van ${fav.userName}`}
                      />
                    );
                  })}

                  {/* Mutual time matches */}
                  {(mutualTimeMatches || []).map((match, idx) => {
                    if (!match?.start || !match?.end) return null;
                    const mStart = new Date(match.start);
                    if (mStart.toDateString() !== date.toDateString()) return null;
                    const mEnd = new Date(match.end);
                    const sh = mStart.getHours();
                    const sm = mStart.getMinutes();
                    const eh = mEnd.getHours();
                    const em = mEnd.getMinutes();
                    const top = (sh - 6) * 25 + (sm / 60) * 25 + 10;
                    const height = Math.max((eh - sh) * 25 + ((em - sm) / 60) * 25, 20);
                    return (
                      <div
                        key={`match-${idx}`}
                        className="absolute left-0.5 right-0.5 z-4 pointer-events-none"
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          background: 'rgba(16, 185, 129, 0.15)',
                          border: '2px solid #10B981',
                          borderRadius: '6px',
                        }}
                      >
                        <span className="text-[9px] text-green-800 font-bold px-1">
                          Match
                        </span>
                      </div>
                    );
                  })}
                          </div>
              ))}
                        </div>
          </div>
        </div>

        {/* Side Panel */}
        {sidePanelOpen && (() => {
          // Centralized counts for panel copy
          const autoplanCount = autoplanState.results?.length ?? 0;
          const proposalCount = proposalSlots.length;
          const availabilityCount = events.filter((e: any) =>
            e.type === 'networking' &&
            e.source === 'free_for_networking' &&
            e.eventType !== 'networking_filled'
          ).length;
          const selectedRangeText = manualSelection 
            ? formatRangeNl(manualSelection.start, manualSelection.end)
            : '';

          // pendingMatch and matchCopy are computed as useMemo hooks above the render.

          const panelCtx: PanelCtx = {
            otherUsername: otherUserName || 'Connectie',
            autoplanCount,
            proposalCount,
            availabilityCount,
            selectedRangeText,
          };

          const panelConfig = PANEL_COPY[planningMode] || PANEL_COPY.none;

          return (
          <div className="w-96 xl:w-[26rem] bg-white border-l border-gray-200 flex-shrink-0 flex flex-col overflow-hidden">
            {/* Side panel header */}
            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">{panelConfig.title}</h3>
              {panelConfig.statusLine(panelCtx) && (
                <p className="text-xs text-gray-500 mt-0.5">{panelConfig.statusLine(panelCtx)}</p>
              )}
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-4" style={{ scrollbarWidth: 'thin' }}>

              {/* Incoming proposals — always shown at top of side panel */}
              {incomingProposals.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    Openstaande voorstellen ({incomingProposals.length})
                  </h4>
                  {incomingProposals.map((proposal) => (
                    <div key={proposal.id} className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                      {/* Sender badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {proposal.fromUserName.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-gray-700 truncate">{proposal.fromUserName}</div>
                          <div className="text-[10px] text-gray-400">
                            {proposal.createdAt.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                      {proposal.message && (
                        <div className="text-xs text-gray-500 italic mb-2 px-1">"{proposal.message}"</div>
                      )}
                      <div className="space-y-1.5 mb-3">
                        {(proposal.proposedDates || []).map((slot, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100 text-xs">
                            <span className="text-gray-600 font-medium">
                              {new Date(slot.date).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                              {' · '}{slot.time}{slot.endTime && ` – ${slot.endTime}`}
                            </span>
                            <button
                              onClick={() => respondToProposal(proposal, true, idx)}
                              className="px-2.5 py-1 bg-emerald-500 text-white rounded-lg text-[10px] font-semibold hover:bg-emerald-600 transition-colors flex items-center gap-1"
                            >
                              <Check size={10} />
                              Kies
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                        <button
                          onClick={() => startCounterProposal(proposal)}
                          className="text-blue-600 text-xs hover:text-blue-700 font-medium flex items-center gap-1"
                        >
                          <RefreshCw size={11} />
                          Tegenvoorstel
                        </button>
                        <button
                          onClick={() => respondToProposal(proposal, false)}
                          className="text-red-500 text-xs hover:text-red-700 font-medium"
                        >
                          Afwijzen
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="border-t border-gray-100" />
                </div>
              )}

              {/* ── UNIFIED MATCH CARD ─────────────────────────────────────────
                  Shown automatically whenever any planning source has a match.
                  Sources (priority order): favorites ★ > autoplan > manual.
                  The card adapts its title/subtitle per source but is otherwise
                  identical — one consistent confirmation experience.           ──── */}
              {pendingMatch && (
                <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', border: '2px solid #22c55e', borderRadius: 16, padding: '14px', boxShadow: '0 4px 16px rgba(34,197,94,0.15)' }}>
                  {/* Source badge */}
                  {pendingMatch.source !== 'manual' && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 20, padding: '2px 8px', marginBottom: 10 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', letterSpacing: '0.03em' }}>
                        {pendingMatch.source === 'favorites' ? '★ Favoriet' : '⚡ Autoplan'}
                      </span>
                    </div>
                  )}

                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#14532d' }}>
                        {matchCopy[pendingMatch.source].title}
                      </p>
                      <p style={{ margin: 0, fontSize: 11, color: '#166534' }}>
                        {matchCopy[pendingMatch.source].subtitle}
                      </p>
                    </div>
                  </div>

                  {/* Location pill */}
                  {localConfirmedLocation && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #bbf7d0', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#15803d', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {localConfirmedLocation.name}
                      </span>
                    </div>
                  )}

                  {/* Slots */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {pendingMatch.slots.map((slot, idx) => {
                      const dateLabel = slot.start.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
                      const timeLabel = `${slot.start.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} – ${slot.end.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
                      const isManual = pendingMatch?.source === 'manual';
                      return (
                        <div key={idx} style={{ background: '#fff', border: '1.5px solid #bbf7d0', borderRadius: 12, padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </div>
                            <div>
                              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>{dateLabel}</p>
                              <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>{timeLabel}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => isManual ? handlePlanManual() : handleAutoplanSelect({ start: slot.start, end: slot.end })}
                            style={{
                              width: '100%', padding: '10px',
                              background: 'linear-gradient(135deg, #16a34a, #15803d)',
                              border: 'none', borderRadius: 10,
                              fontSize: 13, fontWeight: 700, color: '#fff',
                              cursor: 'pointer', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', gap: 6,
                              boxShadow: '0 3px 10px rgba(22,163,74,0.3)',
                            }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg>
                            {idx === 0 ? 'Bevestig afspraak' : 'Kies dit moment'}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Reminder hint */}
                  <p style={{ margin: '10px 0 0', fontSize: 10, color: '#166534', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    Je ontvangt automatisch een herinnering 24 uur van tevoren
                  </p>
                </div>
              )}

              {planningMode === 'manual' && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-900">
                    {panelConfig.instruction}
                  </div>
                  {manualSelection && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
                      <div className="text-xs font-semibold text-green-900">Geselecteerd:</div>
                      <div className="text-sm text-green-800">
                        {selectedRangeText}
                      </div>
                      <button
                        className="w-full px-3 py-2 bg-green-600 text-white rounded text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handlePlanManual}
                        disabled={manualInFlight.current || !manualSelection}
                      >
                        {manualInFlight.current ? 'Bezig...' : 'Plan afspraak'}
                      </button>
                  </div>
                )}
                  {!manualSelection && (
                    <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded p-2">
                      {panelConfig.emptyState}
              </div>
            )}
          </div>
              )}

              {planningMode === 'autoplan' && (
                <div className="space-y-3">

                  {/* Loading state */}
                  {autoplanState.loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '24px 16px', background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 14 }}>
                      <span style={{ width: 28, height: 28, border: '3px solid #bfdbfe', borderTopColor: '#2563eb', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                      <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', margin: 0 }}>Overlappende momenten zoeken…</p>
                        <p style={{ fontSize: 11, color: '#3b82f6', margin: '4px 0 0' }}>Jullie "Vrij voor netwerken" blokken worden vergeleken</p>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {autoplanState.error && !autoplanState.loading && (
                    <div style={{ padding: '14px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/><circle cx="12" cy="12" r="10"/></svg>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#c2410c' }}>Geen overlapping gevonden</p>
                      </div>
                      <p style={{ fontSize: 11, color: '#9a3412', margin: '0 0 12px', lineHeight: 1.5 }}>
                        Zet meer "Vrij voor netwerken" blokken in je agenda op momenten dat jullie beiden vrij zijn.
                      </p>
                      <button
                        onClick={() => { setPlanningMode('proposal'); setSidePanelOpen(true); }}
                        style={{ width: '100%', padding: '9px', background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#c2410c', cursor: 'pointer' }}
                      >
                        Plan handmatig
                      </button>
                    </div>
                  )}

                  {/* No results (after search, no error) */}
                  {!autoplanState.loading && !autoplanState.error && autoplanState.results.length === 0 && (
                    <div style={{ padding: '14px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#f97316', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2.5}><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/><circle cx="12" cy="12" r="10"/></svg>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#c2410c' }}>Geen match gevonden</p>
                      </div>
                      <p style={{ fontSize: 11, color: '#9a3412', margin: '0 0 12px', lineHeight: 1.5 }}>
                        Jullie "Vrij voor netwerken" blokken overlappen niet. Voeg meer beschikbaarheid toe of plan handmatig.
                      </p>
                      <button
                        onClick={() => { setPlanningMode('proposal'); setSidePanelOpen(true); }}
                        style={{ width: '100%', padding: '9px', background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 10, fontSize: 12, fontWeight: 700, color: '#c2410c', cursor: 'pointer' }}
                      >
                        Handmatig plannen
                      </button>
                    </div>
                  )}

                  {/* Results found — prominent match cards */}
                  {!autoplanState.loading && autoplanState.results.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Success header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', border: '1.5px solid #86efac', borderRadius: 12 }}>
                        <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                            {autoplanState.results.length} match{autoplanState.results.length > 1 ? 'es' : ''} gevonden!
                          </p>
                          <p style={{ margin: 0, fontSize: 11, color: '#166534' }}>Kies een moment en plan direct</p>
                        </div>
                      </div>

                      {/* Match cards */}
                      {autoplanState.results.map((slot: any, idx: number) => (
                        <div key={idx} style={{ background: '#fff', border: '1.5px solid #bbf7d0', borderRadius: 14, padding: '14px', boxShadow: '0 2px 8px rgba(22,163,74,0.1)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            </div>
                            <div>
                              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>{formatSlotLabel(slot)}</p>
                              <p style={{ margin: '2px 0 0', fontSize: 11, color: '#6b7280' }}>Met {otherUserName || 'Connectie'}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleAutoplanSelect(slot)}
                            disabled={autoplanSelectInFlight.current}
                            style={{
                              width: '100%',
                              padding: '11px',
                              background: autoplanSelectInFlight.current ? '#dcfce7' : 'linear-gradient(135deg, #16a34a, #15803d)',
                              border: 'none',
                              borderRadius: 10,
                              fontSize: 13,
                              fontWeight: 700,
                              color: autoplanSelectInFlight.current ? '#16a34a' : '#fff',
                              cursor: autoplanSelectInFlight.current ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 6,
                              boxShadow: '0 3px 10px rgba(22,163,74,0.3)',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            {autoplanSelectInFlight.current
                              ? <><span style={{ width: 14, height: 14, border: '2px solid #86efac', borderTopColor: '#16a34a', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Plannen…</>
                              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="20 6 9 17 4 12"/></svg> Plan afspraak</>
                            }
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {planningMode === 'proposal' && (
                <div className="space-y-4" ref={proposalPanelRef}>

                  {/* ── STATUS HEADER ── */}
                  <div className="flex items-center justify-between text-[11px] text-gray-500">
                    <span>
                      Jij: <strong className="text-gray-700">{proposalSlots.length}/3</strong>
                      {' · '}
                      {otherUserName || 'Connectie'}: <strong className="text-gray-700">{otherUserLiveSlots.length}/3</strong>
                    </span>
                    {proposalSlots.length < 3 && (
                      <span className="text-[10px] text-gray-400">klik op kalender om te voegen</span>
                    )}
                  </div>

                  {proposalError && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded p-2">
                      {proposalError}
                    </div>
                  )}

                  {/* ── UNIFIED 6-BLOCK LIST ──────────────────────────────────────────
                      My blocks:  stone background, "Jij" badge, trash icon
                      Their blocks (not starred): gray, empty star ☆ — click to star
                      Their blocks (starred):     green, filled star ★ — click to unstar
                  ─────────────────────────────────────────────────────────────────── */}
                  <div className="space-y-1.5">

                    {/* My blocks */}
                    {proposalSlots.map((slot, idx) => (
                      <div key={`mine-${idx}`} className="rounded-xl px-3 py-2.5 border border-stone-200 bg-stone-50 text-xs flex items-center gap-2">
                        <span className="text-[9px] font-bold text-stone-500 bg-stone-200 px-1.5 py-0.5 rounded-full flex-shrink-0 uppercase tracking-wide">Jij</span>
                        {editingSlotIndex === idx ? (
                          <div className="flex-1 space-y-1.5">
                            <div className="grid grid-cols-3 gap-1.5">
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Datum</label>
                                <input type="date" value={editingSlotData?.date || slot.start.split('T')[0]}
                                  onChange={(e) => setEditingSlotData({ ...editingSlotData!, date: e.target.value })}
                                  className="w-full text-[10px] px-1 py-0.5 border border-gray-300 rounded" />
                              </div>
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Van</label>
                                <input type="time" value={editingSlotData?.startTime || slot.start.split('T')[1].slice(0,5)}
                                  onChange={(e) => setEditingSlotData({ ...editingSlotData!, startTime: e.target.value })}
                                  className="w-full text-[10px] px-1 py-0.5 border border-gray-300 rounded" />
                              </div>
                              <div>
                                <label className="block text-[9px] text-gray-500 mb-0.5">Tot</label>
                                <input type="time" value={editingSlotData?.endTime || slot.end.split('T')[1].slice(0,5)}
                                  onChange={(e) => setEditingSlotData({ ...editingSlotData!, endTime: e.target.value })}
                                  className="w-full text-[10px] px-1 py-0.5 border border-gray-300 rounded" />
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => {
                                if (editingSlotData) {
                                  const newStart = new Date(`${editingSlotData.date}T${editingSlotData.startTime}`);
                                  const newEnd = new Date(`${editingSlotData.date}T${editingSlotData.endTime}`);
                                  if (newEnd <= newStart) { showToast('Eindtijd moet na starttijd zijn', 'error'); return; }
                                  const updated = [...proposalSlots];
                                  updated[idx] = { start: newStart.toISOString(), end: newEnd.toISOString() };
                                  setProposalSlots(updated);
                                  showToast('Tijd aangepast', 'success');
                                }
                                setEditingSlotIndex(null); setEditingSlotData(null);
                              }} className="flex-1 bg-green-500 text-white px-2 py-0.5 rounded text-[10px] hover:bg-green-600">✓ Opslaan</button>
                              <button onClick={() => { setEditingSlotIndex(null); setEditingSlotData(null); }}
                                className="flex-1 bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-[10px] hover:bg-gray-300">Annuleer</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => { setEditingSlotIndex(idx); setEditingSlotData({ date: slot.start.split('T')[0], startTime: slot.start.split('T')[1].slice(0,5), endTime: slot.end.split('T')[1].slice(0,5) }); }}
                              className="flex-1 text-left text-gray-700 truncate hover:text-blue-600 transition-colors"
                              title="Klik om tijd aan te passen"
                            >{formatRangeNl(new Date(slot.start), new Date(slot.end))}</button>
                            <button
                              onClick={() => { setProposalSlots(proposalSlots.filter((_, i) => i !== idx)); }}
                              className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors p-0.5"
                              title="Verwijderen"
                            >
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}

                    {proposalSlots.length === 0 && (
                      <div className="text-xs text-gray-400 italic py-1 px-1">
                        Klik op de kalender om jouw beschikbare momenten toe te voegen
                      </div>
                    )}

                    {/* Divider between sections when both have items */}
                    {proposalSlots.length > 0 && otherUserLiveSlots.length > 0 && (
                      <div className="border-t border-gray-100 pt-0.5" />
                    )}

                    {/* Other user's blocks — click to favorite/unfavorite */}
                    {otherUserLiveSlots.map((slot, idx) => {
                      const starred = isFavorite(slot.start, slot.end);
                      return (
                        <div
                          key={`theirs-${idx}`}
                          className={`rounded-xl px-3 py-2.5 border text-xs flex items-center gap-2 cursor-pointer transition-all select-none ${
                            starred
                              ? 'bg-green-50 border-green-300 hover:bg-green-100'
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                          onClick={() => toggleTimeSlotFavorite(slot.start, slot.end)}
                          title={starred ? 'Klik om te verwijderen als favoriet' : 'Klik om als favoriet te markeren — dit is jouw stem voor dit moment'}
                        >
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 uppercase tracking-wide ${
                            starred ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-500'
                          }`}>
                            {otherUserName?.split(' ')[0] || 'Ander'}
                          </span>
                          <span className={`flex-1 truncate font-medium ${starred ? 'text-green-900' : 'text-gray-600'}`}>
                            {formatRangeNl(new Date(slot.start), new Date(slot.end))}
                          </span>
                          <Star
                            size={15}
                            fill={starred ? '#16a34a' : 'none'}
                            color={starred ? '#16a34a' : '#d1d5db'}
                            className="flex-shrink-0 transition-all"
                          />
                        </div>
                      );
                    })}

                    {otherUserLiveSlots.length === 0 && (
                      <div className="text-xs text-gray-400 italic py-1 px-1">
                        {otherUserName || 'Connectie'} heeft nog geen blokken toegevoegd
                      </div>
                    )}
                  </div>

                  {/* ── VERSTUUR VOORSTEL (secondary action) ── */}
                  {proposalSlots.length >= 1 && (
                    <button
                      onClick={handleSendProposal}
                      disabled={sendingProposal}
                      className="w-full py-2.5 px-4 rounded-xl text-xs font-semibold text-white transition-all flex items-center justify-center gap-2"
                      style={{
                        background: 'linear-gradient(135deg, #7A9488 0%, #4F6660 100%)',
                        opacity: sendingProposal ? 0.7 : 1,
                        cursor: sendingProposal ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {sendingProposal ? (
                        <><span className="animate-spin">⟳</span> Versturen…</>
                      ) : (
                        <><Send size={13} /> Verstuur voorstel naar {otherUserName || 'connectie'}</>
                      )}
                    </button>
                  )}

                </div>
              )}

              {planningMode === 'availabilityOnly' && (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-100 rounded-lg p-3 text-sm text-green-900">
                    {panelConfig.instruction}
                  </div>
                  {availabilityCount > 0 ? (
                    <div className="text-xs text-gray-700">
                      Er zijn {availabilityCount} beschikbare momenten in deze periode.
                        </div>
                      ) : (
                    <div className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded p-2">
                      {panelConfig.emptyState}
                        </div>
                      )}
                    </div>
              )}

              {/* Mutual Time Matches (removed — unified card at top replaces this) */}
              {false && mutualTimeMatches.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-green-900 mb-2 flex items-center gap-1">
                    <Check size={14} className="text-green-600" />
                    Wederzijdse tijdmatches ({mutualTimeMatches.length})
                  </h4>
                  <div className="space-y-1">
                    {mutualTimeMatches.map((match, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-white rounded px-2 py-1.5 border border-green-100 text-xs"
                      >
                        <span className="text-gray-800">
                          {formatRangeNl(new Date(match.start), new Date(match.end))}
                        </span>
                        <button
                          onClick={() => {
                            const newSlot = { start: match.start, end: match.end };
                            if (proposalSlots.length >= 3) {
                              showToast('Maximaal 3 momenten', 'info');
                              return;
                            }
                            setProposalSlots([...proposalSlots, newSlot]);
                            setPlanningMode('proposal');
                            showToast('Tijdmatch toegevoegd aan voorstel', 'success');
                          }}
                          className="px-2 py-0.5 bg-green-600 text-white rounded text-[10px] hover:bg-green-700"
                        >
                          Gebruik
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorites summary — hidden: stars are now shown inline in the unified block list */}
              {false && (timeSlotFavorites.length > 0 || otherUserFavorites.length > 0) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">
                    Favoriete tijden
                  </h4>
                  {timeSlotFavorites.length > 0 && (
                    <div className="mb-2">
                      <span className="text-[10px] text-gray-500 font-medium">Jouw favorieten:</span>
                      <div className="space-y-1 mt-1">
                        {timeSlotFavorites.map((fav) => (
                          <div key={fav.id} className="flex items-center justify-between text-[11px] bg-white rounded px-2 py-1 border border-gray-100">
                            <span className="text-gray-700">
                              {formatRangeNl(new Date(fav.start), new Date(fav.end))}
                            </span>
                            <button
                              onClick={() => toggleTimeSlotFavorite(fav.start, fav.end)}
                              className="text-[#7A9488] hover:text-[#4F6660]"
                              title="Favoriet verwijderen"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {otherUserFavorites.length > 0 && (
                    <div>
                      <span className="text-[10px] text-gray-500 font-medium">
                        Favorieten van {otherUserName}:
                      </span>
                      <div className="space-y-1 mt-1">
                        {otherUserFavorites.map((fav) => (
                          <div key={fav.id} className="text-[11px] bg-blue-50 rounded px-2 py-1 border border-blue-100 text-blue-800">
                            {formatRangeNl(new Date(fav.start), new Date(fav.end))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Favorite toggle hint */}
              {planningMode !== 'none' && (
                <div className="text-[10px] text-gray-400 bg-gray-50 rounded p-2">
                  Tip: dubbelklik op een beschikbaarheidslot om het als favoriet te markeren.
                </div>
              )}

                </div>
            </div>
          );
        })()}
          </div>

      {/* COMMUNICATION BAR — always visible when connection is active */}
      {connectionId && user?.uid && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 flex items-center gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <PlanningChat
              connectionId={connectionId}
              currentUserId={user.uid}
              currentUserName={user.displayName || 'Jij'}
              otherUserName={otherUserName}
            />
          </div>
          <div className="flex-shrink-0 w-52">
            <PlanningAudioChannel
              connectionId={connectionId}
              currentUserId={user.uid}
              currentUserName={user.displayName || 'Jij'}
              otherUserName={otherUserName}
            />
          </div>
        </div>
      )}


      {/* PROPOSAL MODAL */}
      {showProposalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Calendar size={20} className="text-[#7A9488]" />
                  Tijd Voorstellen
                </h3>
                <button
                  onClick={() => setShowProposalModal(false)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
        </div>

              {/* Recipient Info */}
              {otherUserName && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                  <UserIcon size={16} className="text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <span className="font-medium">Voor:</span> {otherUserName}
                  </span>
      </div>
              )}

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bericht (optioneel)
                </label>
                <textarea
                  value={proposalMessage}
                  onChange={(e) => setProposalMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-[#7A9488] resize-none"
                  rows={2}
                  placeholder="Bijv. 'Zullen we een call inplannen?'"
                />
            </div>

              {/* Proposed Dates */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    Voorgestelde Tijden
                  </label>
                  <button
                    onClick={addProposedDate}
                    className="text-[#7A9488] hover:text-[#4F6660] text-sm font-medium flex items-center gap-1"
                  >
                    <span className="text-lg">+</span>
                    Toevoegen
                  </button>
            </div>

                <div className="space-y-3">
                  {proposedDates.map((proposal, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="grid grid-cols-4 gap-2">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-600 mb-1">Datum</label>
                          <input
                            type="date"
                            value={proposal.date}
                            onChange={(e) => updateProposedDate(index, 'date', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#7A9488]"
                          />
            </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Start</label>
                          <input
                            type="time"
                            value={proposal.time}
                            onChange={(e) => updateProposedDate(index, 'time', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#7A9488]"
                          />
          </div>
                        <div className="flex items-end gap-1">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">Eind</label>
                            <input
                              type="time"
                              value={proposal.endTime || ''}
                              onChange={(e) => updateProposedDate(index, 'endTime', e.target.value)}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-[#7A9488]"
                            />
                          </div>
                          {proposedDates.length > 1 && (
            <button
                              onClick={() => removeProposedDate(index)}
                              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors mb-0.5"
            >
                              <X size={16} />
            </button>
                          )}
          </div>
                      </div>
                    </div>
                  ))}
        </div>
      </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={sendProposal}
                  disabled={proposalLoading || !proposedDates.some(p => p.date && p.time)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    proposalLoading || !proposedDates.some(p => p.date && p.time)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#7A9488] text-white hover:bg-[#4F6660]'
                  }`}
                >
                  {proposalLoading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <Send size={16} />
                  )}
                  Versturen
                </button>
                <button
                  onClick={() => setShowProposalModal(false)}
                  className="flex-1 bg-gray-100 text-gray-600 py-2.5 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MONTH VIEW / ADD EVENT MODAL */}
      {showMonthView && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {showTimeInput
                  ? `${selectedDate?.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' })}`
                  : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                }
              </h2>
              <button
                onClick={() => {
                  setShowMonthView(false);
                  setShowTimeInput(false);
                  setSelectedDate(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <X size={24} />
              </button>
            </div>

            {!showTimeInput ? (
              <>
                {/* Color Picker */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Kleur:</h3>
                  <div className="flex space-x-2">
                    {colors.map((color) => (
                      <button
                        key={color.value}
                        onClick={() => setSelectedColor(color.value)}
                        className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                          selectedColor === color.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {daysOfWeek.map((day) => (
                    <div key={day} className="p-2 text-center font-medium text-gray-600 text-sm">
                      {day}
                    </div>
                  ))}
                  
                  {getMonthDates(currentDate).map((dayInfo, index) => {
                    const hasEvents = getEventsForDate(dayInfo.date).length > 0;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => dayInfo.isCurrentMonth && handleDateClick(dayInfo.date)}
                        className={`p-2 text-center border border-gray-200 hover:bg-stone-50 transition-colors relative rounded ${
                          !dayInfo.isCurrentMonth ? 'text-gray-300' : 'text-gray-900'
                        } ${isToday(dayInfo.date) ? 'bg-stone-100 font-bold' : ''}`}
                        disabled={!dayInfo.isCurrentMonth}
                      >
                        {dayInfo.date.getDate()}
                        {hasEvents && (
                          <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              /* Time Input Form */
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                  <input
                    type="text"
                    value={newEventTitle}
                    onChange={(e) => setNewEventTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
                    placeholder="Wat is de afspraak?"
                    autoFocus
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Starttijd</label>
                    <input
                      type="time"
                      value={newEventStartTime}
                      onChange={(e) => setNewEventStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Eindtijd</label>
                    <input
                      type="time"
                      value={newEventEndTime}
                      onChange={(e) => setNewEventEndTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
                    />
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={addNewEvent}
                    disabled={!newEventTitle || !newEventStartTime || !newEventEndTime}
                    className="px-6 py-2 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Toevoegen
                  </button>
                  <button
                    onClick={() => setShowTimeInput(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Terug
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* EDIT EVENT MODAL */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Event Bewerken</h3>
              <button onClick={() => setEditingEvent(null)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
                </button>
              </div>
              
            <div className="space-y-4">
                        <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel</label>
                          <input
                  type="text"
                  value={editingEvent.title}
                  onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
                          />
                        </div>

                        <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Beschrijving</label>
                <textarea
                  value={editingEvent.description || ''}
                  onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488]"
                  rows={3}
                />
              </div>
              
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={saveEvent}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                >
                  Opslaan
                </button>
                <button
                  onClick={() => setEditingEvent(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE RECURRING EVENT MODAL */}
      {showDeleteModal && eventToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2"><Trash2 size={18} className="text-red-500" /> Afspraak Verwijderen</h3>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setEventToDelete(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700">
                Dit is een herhalende afspraak. Wat wil je verwijderen?
              </p>

              <div className="space-y-3">
                <button
                  onClick={async () => {
                    await deleteEvent(eventToDelete.id, 'single', eventToDelete.startTime);
                    setShowDeleteModal(false);
                    setEventToDelete(null);
                  }}
                  className="w-full px-4 py-3 bg-[#7A9488] text-white rounded-lg hover:bg-[#4F6660] transition-colors font-medium text-left"
                >
                  <div className="font-semibold">Optie 1: Alleen deze afspraak</div>
                  <div className="text-sm opacity-90">Verwijdert alleen deze specifieke occurrence</div>
                </button>

                <button
                  onClick={async () => {
                    await deleteEvent(eventToDelete.id, 'series');
                    setShowDeleteModal(false);
                    setEventToDelete(null);
                  }}
                  className="w-full px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-left"
                >
                  <div className="font-semibold">Optie 2: Hele reeks verwijderen</div>
                  <div className="text-sm opacity-90">Verwijdert alle herhaalde afspraken</div>
                </button>
              </div>

              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setEventToDelete(null);
                }}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST NOTIFICATION - Enhanced for appointment confirmations */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-[60] px-5 py-4 rounded-lg shadow-xl flex items-center gap-3 min-w-[280px] max-w-[400px] ${
            toast.type === 'success'
              ? 'bg-green-500 text-white border-2 border-green-600'
              : toast.type === 'error'
              ? 'bg-red-500 text-white border-2 border-red-600'
              : 'bg-blue-500 text-white border-2 border-blue-600'
          }`}
          style={{
            animation: 'slideUp 0.3s ease-out',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}
        >
          {toast.type === 'success' && <Check size={20} className="flex-shrink-0" />}
          {toast.type === 'error' && <X size={20} className="flex-shrink-0" />}
          {toast.type === 'info' && <AlertCircle size={20} className="flex-shrink-0" />}
          <span className="text-sm font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      {/* ── Connection picker floating dropdown (fixed, never shifts layout) ── */}
      {showConnectionPicker && pickerDropPos && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={() => setShowConnectionPicker(false)}
          />
          <div
            style={{
              position: 'fixed',
              top: pickerDropPos.top,
              left: pickerDropPos.left,
              width: pickerDropPos.width,
              zIndex: 9999,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 14,
              boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.06)',
              overflow: 'hidden',
            }}
          >
            {/* Search */}
            <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #f3f4f6' }}>
              <input
                autoFocus
                type="text"
                placeholder="Zoek connectie…"
                value={connectionSearch}
                onChange={(e) => setConnectionSearch(e.target.value)}
                style={{
                  width: '100%',
                  fontSize: 12,
                  padding: '7px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  outline: 'none',
                  background: '#f9fafb',
                  boxSizing: 'border-box',
                }}
                onFocus={e => { e.target.style.borderColor = '#7A9488'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
              />
            </div>

            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {userConnections.length > 0 && (
                <div>
                  <div style={{ padding: '8px 12px 4px' }}>
                    <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9ca3af' }}>
                      Connecties
                    </span>
                  </div>
                  {userConnections
                    .filter(c =>
                      !connectionSearch ||
                      c.name.toLowerCase().includes(connectionSearch.toLowerCase()) ||
                      (c.company || '').toLowerCase().includes(connectionSearch.toLowerCase())
                    )
                    .map((conn) => {
                      const isActive = activeConnectionData?.name === conn.name;
                      const initials = conn.name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
                      return (
                        <button
                          key={conn.id}
                          onClick={() => {
                            setInternalConnectionData({
                              id: conn.id,
                              name: conn.name,
                              photoURL: conn.photoURL,
                              user1Id: user?.uid || '',
                              user2Id: conn.userId,
                            });
                            setProposalSlots([]);
                            setOtherUserLiveSlots([]);
                            setMutualTimeMatches([]);
                            setPlanningMode('none');
                            setConnectionSearch('');
                            setShowConnectionPicker(false);
                            showToast(`Plannen met ${conn.name}`, 'info');
                          }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '8px 12px',
                            background: isActive ? '#f0f4ea' : 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            textAlign: 'left',
                            transition: 'background 0.1s',
                          }}
                          onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
                          onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          {/* Avatar — consistent circle with initials */}
                          <div style={{
                            flexShrink: 0,
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            background: '#dbeafe',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1.5px solid #fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                          }}>
                            {conn.photoURL ? (
                              <img src={conn.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#2563eb', lineHeight: 1 }}>{initials}</span>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conn.name}</div>
                            {conn.company && (
                              <div style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{conn.company}</div>
                            )}
                          </div>
                          {isActive && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7A9488" strokeWidth={3}><polyline points="20 6 9 17 4 12"/></svg>
                          )}
                        </button>
                      );
                    })}
                  {userConnections.filter(c =>
                    !connectionSearch ||
                    c.name.toLowerCase().includes(connectionSearch.toLowerCase()) ||
                    (c.company || '').toLowerCase().includes(connectionSearch.toLowerCase())
                  ).length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                      Geen resultaten
                    </div>
                  )}
                </div>
              )}
              {userConnections.length === 0 && !connectionSearch && (
                <div style={{ padding: '12px', fontSize: 11, color: '#9ca3af', fontStyle: 'italic', textAlign: 'center' }}>
                  Geen actieve connecties gevonden
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes sk-match-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
          50%  { box-shadow: 0 0 0 10px rgba(34,197,94,0);   }
          100% { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ExtendedAgenda;
